// src/screens/Planner.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
// LEDGER skin (June 2026, approved mix: Ledger look + Ops task chips)
const SERIF_LG = 'Georgia, "Times New Roman", serif';
const PAPER_LG = '#f7f4ee';
const PAPER_DEEP_LG = '#efe9db';
const INK_LG = '#1a2e38';
const GOLD_LG = '#9F7E4F';
const GOLD_HAIR_LG = 'rgba(159,126,79,0.28)';

// ── Real team from Supabase team_members table ───────────────────────────────────────
const MEMBER_COLORS = ['#314F5C','#6F8FA7','#9F7E4F','#819D84','#B99C6D','#204C2C','#5a7a8a'];
function getInitials(name) {
  return (name || '').split(/\s+/).map(function(w) {
    var c = w.replace(/[^a-zA-Z]/g, ''); return c ? c[0].toUpperCase() : '';
  }).join('').slice(0, 2) || '??';
}
function useTeamMembers() {
  const [team, setTeam] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('team_members')
          .select('name, email, color, timezone')
          .eq('active', true)
          .order('created_at');
        if (!cancelled && data) {
          setTeam(data.map(function(m, i) {
            return {
              initials: getInitials(m.name),
              name: m.name,
              email: m.email,
              color: m.color || MEMBER_COLORS[i % MEMBER_COLORS.length],
              text: '#fff',
              timezone: m.timezone || null,
            };
          }));
        }
      } catch (_e) { console.error('Planner error:', _e); }
    })();
    return function() { cancelled = true; };
  }, []);
  return team;
}

const EVENT_TYPES = [
  { id: 'meeting',  label: 'Meeting',         color: '#1e4d5e', bg: 'rgba(49,79,92,0.11)',   solid: '#314F5C' },
  { id: 'pipeline', label: 'Pipeline',         color: '#2d6b38', bg: 'rgba(60,120,72,0.12)',  solid: '#3c7848' },
  { id: 'travel',   label: 'Travel',           color: '#6b440f', bg: 'rgba(138,98,48,0.13)',  solid: '#8a6230' },
  { id: 'launch',   label: 'Launch',           color: '#134f82', bg: 'rgba(25,100,170,0.12)', solid: '#1964aa' },
  { id: 'content',  label: 'Content Deadline', color: '#5c2e00', bg: 'rgba(130,70,10,0.12)',  solid: '#82460a' },
];

const COL_CFG = [
  { id: 'todo',       label: 'To Do',       color: '#6F8FA7' },
  { id: 'inprogress', label: 'In Progress',  color: '#B99C6D' },
  { id: 'done',       label: 'Done',         color: '#819D84' },
  { id: 'blocked',    label: 'Blocked',      color: '#b0bcC4' },
];

const TAG_MAP = {
  Data:      { bg: 'rgba(49,79,92,0.08)',    color: '#2a5060' },
  Pipeline:  { bg: 'rgba(129,157,132,0.14)', color: '#3a5d3e' },
  Launch:    { bg: 'rgba(49,79,92,0.08)',    color: '#2a5060' },
  iOS:       { bg: 'rgba(111,143,167,0.11)', color: '#3a5f78' },
  Auth:      { bg: 'rgba(49,79,92,0.08)',    color: '#2a5060' },
  Backend:   { bg: 'rgba(129,157,132,0.14)', color: '#3a5d3e' },
  Marketing: { bg: 'rgba(159,126,79,0.11)',  color: '#6b4f1e' },
  Frontend:  { bg: 'rgba(111,143,167,0.11)', color: '#3a5f78' },
  Android:   { bg: 'rgba(111,143,167,0.11)', color: '#3a5f78' },
  Design:    { bg: 'rgba(159,126,79,0.11)',  color: '#6b4f1e' },
};

const PRI_STYLE = {
  p1: { bg: 'rgba(210,50,50,0.09)',   color: '#ad2929', dot: '#d94f4f', label: 'P1', border: 'rgba(210,50,50,0.22)'   },
  p2: { bg: 'rgba(200,120,30,0.09)',  color: '#8a5000', dot: '#c8883a', label: 'P2', border: 'rgba(200,120,30,0.22)'  },
  p3: { bg: 'rgba(60,140,90,0.08)',   color: '#286040', dot: '#5fa870', label: 'P3', border: 'rgba(60,140,90,0.2)'    },
};

const RECUR_OPTS = ['Once', 'Weekly', 'Bi-weekly', 'Monthly'];
const CAL_VIEWS  = ['Month', 'Week', 'Day', 'Agenda'];
const HOURS      = Array.from({ length: 24 }, (_, i) => i); // 0–23, full day
const ROW_H      = 64;
const DURATIONS  = [15, 30, 45, 60, 90, 120, 150, 180, 240]; // minutes, for the EventForm duration picker
const durLabel   = m => m < 60 ? m + ' min' : (m % 60 === 0 ? (m / 60) + (m === 60 ? ' hour' : ' hours') : Math.floor(m / 60) + 'h ' + (m % 60) + 'm');

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmtTime(h, m = 0) {
  const s = h >= 12 ? 'PM' : 'AM';
  const hh = h > 12 ? h - 12 : h === 0 ? 12 : h;
  // Clean format: "9 AM" for on-the-hour, "9:30 AM" otherwise
  if (m === 0) return hh + ' ' + s;
  return hh + ':' + String(m).padStart(2, '0') + ' ' + s;
}
function fmtDateLabel(date) {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtShort(date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  d.setHours(0, 0, 0, 0);
  return d;
}
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function fmtTaskDate(d) {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function isOverdue(d) {
  if (!d) return false;
  return new Date(d + 'T00:00:00') < new Date(new Date().toDateString());
}
// Returns YYYY-MM-DD in the browser's LOCAL timezone (not UTC)
function localDateStr(date) {
  const d = new Date(date);
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
}
function getTypeStyle(typeId) {
  return EVENT_TYPES.find(t => t.id === typeId) || EVENT_TYPES[0];
}

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

// ── Avatar ────────────────────────────────────────────────────────────────────
function Av({ initials, color, size = 22 }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  return (
    <div style={{ width: size, height: size, borderRadius: Math.round(size * 0.3), background: color || '#314F5C', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.36, fontWeight: 800, color: 'white', flexShrink: 0, fontFamily: F }}>
      {initials}
    </div>
  );
}

// ── Priority badge ────────────────────────────────────────────────────────────
function PriBadge({ p }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const s = PRI_STYLE[p] || { bg: '#eee', color: '#666', label: '' };
  return (
    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 4, letterSpacing: '.3px', background: s.bg, color: s.color, border: '1px solid ' + s.border, flexShrink: 0, fontFamily: F }}>
      {s.label}
    </span>
  );
}


const TIMEZONES = [
  // ── UTC ──────────────────────────────────────────────────────────────────
  { label: 'UTC  (GMT+0)',                          value: 'UTC' },
  // ── Europe ───────────────────────────────────────────────────────────────
  { label: 'London, Lisbon  (GMT+0/+1)',             value: 'Europe/London' },
  { label: 'Azores  (GMT-1/0)',                      value: 'Atlantic/Azores' },
  { label: 'Berlin, Paris, Rome, Madrid  (GMT+1/+2)',value: 'Europe/Paris' },
  { label: 'Amsterdam, Brussels, Warsaw  (GMT+1/+2)',value: 'Europe/Amsterdam' },
  { label: 'Helsinki, Kyiv, Tallinn  (GMT+2/+3)',    value: 'Europe/Helsinki' },
  { label: 'Athens, Bucharest  (GMT+2/+3)',           value: 'Europe/Athens' },
  { label: 'Istanbul  (GMT+3)',                       value: 'Europe/Istanbul' },
  { label: 'Moscow, St. Petersburg  (GMT+3)',         value: 'Europe/Moscow' },
  // ── Africa ───────────────────────────────────────────────────────────────
  { label: 'Casablanca, Dakar  (GMT+0/+1)',           value: 'Africa/Casablanca' },
  { label: 'Lagos, Accra, Abidjan  (GMT+1)',          value: 'Africa/Lagos' },
  { label: 'Cairo, Johannesburg, Nairobi  (GMT+2)',   value: 'Africa/Cairo' },
  { label: 'Nairobi, Addis Ababa  (GMT+3)',           value: 'Africa/Nairobi' },
  // ── Middle East ──────────────────────────────────────────────────────────
  { label: 'Riyadh, Kuwait, Baghdad  (GMT+3)',        value: 'Asia/Riyadh' },
  { label: 'Tehran  (GMT+3:30/+4:30)',                value: 'Asia/Tehran' },
  { label: 'Dubai, Abu Dhabi  (GMT+4)',               value: 'Asia/Dubai' },
  { label: 'Kabul  (GMT+4:30)',                       value: 'Asia/Kabul' },
  // ── South & Central Asia ─────────────────────────────────────────────────
  { label: 'Karachi, Islamabad  (GMT+5)',             value: 'Asia/Karachi' },
  { label: 'Mumbai, New Delhi  (GMT+5:30)',           value: 'Asia/Kolkata' },
  { label: 'Colombo  (GMT+5:30)',                     value: 'Asia/Colombo' },
  { label: 'Kathmandu  (GMT+5:45)',                   value: 'Asia/Kathmandu' },
  { label: 'Dhaka, Almaty  (GMT+6)',                  value: 'Asia/Dhaka' },
  { label: 'Yangon, Rangoon  (GMT+6:30)',             value: 'Asia/Rangoon' },
  { label: 'Tashkent  (GMT+5)',                       value: 'Asia/Tashkent' },
  // ── Southeast & East Asia ────────────────────────────────────────────────
  { label: 'Bangkok, Hanoi, Jakarta  (GMT+7)',        value: 'Asia/Bangkok' },
  { label: 'Phnom Penh, Ho Chi Minh  (GMT+7)',        value: 'Asia/Ho_Chi_Minh' },
  { label: 'Singapore, KL, Manila  (GMT+8)',          value: 'Asia/Singapore' },
  { label: 'Hong Kong, Taipei  (GMT+8)',              value: 'Asia/Hong_Kong' },
  { label: 'Shanghai, Beijing, Chongqing  (GMT+8)',   value: 'Asia/Shanghai' },
  { label: 'Perth  (GMT+8)',                          value: 'Australia/Perth' },
  { label: 'Tokyo, Seoul, Osaka  (GMT+9)',            value: 'Asia/Tokyo' },
  { label: 'Darwin  (GMT+9:30)',                      value: 'Australia/Darwin' },
  { label: 'Brisbane  (GMT+10)',                      value: 'Australia/Brisbane' },
  { label: 'Sydney, Melbourne, Canberra  (GMT+10/+11)',value: 'Australia/Sydney' },
  { label: 'Guam, Port Moresby  (GMT+10)',            value: 'Pacific/Guam' },
  { label: 'Vladivostok  (GMT+10)',                   value: 'Asia/Vladivostok' },
  { label: 'Auckland, Wellington  (GMT+12/+13)',      value: 'Pacific/Auckland' },
  { label: 'Fiji  (GMT+12)',                          value: 'Pacific/Fiji' },
  { label: "Samoa  (GMT+13)",                         value: 'Pacific/Apia' },
  // ── Americas ─────────────────────────────────────────────────────────────
  { label: 'Buenos Aires, Santiago  (GMT-3)',         value: 'America/Argentina/Buenos_Aires' },
  { label: 'Sao Paulo, Brasilia  (GMT-3)',            value: 'America/Sao_Paulo' },
  { label: 'Caracas  (GMT-4)',                        value: 'America/Caracas' },
  { label: 'La Paz, Manaus  (GMT-4)',                 value: 'America/La_Paz' },
  { label: 'Lima, Bogota, Quito  (GMT-5)',            value: 'America/Lima' },
  { label: 'New York, Miami, Toronto  (GMT-5/-4)',    value: 'America/New_York' },
  { label: 'Mexico City, Bogota  (GMT-6/-5)',         value: 'America/Mexico_City' },
  { label: 'Chicago, Winnipeg  (GMT-6/-5)',           value: 'America/Chicago' },
  { label: 'Denver, Salt Lake City  (GMT-7/-6)',      value: 'America/Denver' },
  { label: 'Phoenix  (GMT-7, no DST)',                value: 'America/Phoenix' },
  { label: 'Los Angeles, Vancouver  (GMT-8/-7)',      value: 'America/Los_Angeles' },
  { label: 'Anchorage, Juneau  (GMT-9/-8)',           value: 'America/Anchorage' },
  { label: 'Honolulu  (GMT-10)',                      value: 'Pacific/Honolulu' },
];

// ── Current time hook ─────────────────────────────────────────────────────────
function useCurrentTime() {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  return now;
}

// ── Attendee search picker — scales to any team size ────────────────────────
function AttendeeSearch({ team, attendees, onAdd }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [query, setQuery] = React.useState('');
  const [open,  setOpen]  = React.useState(false);
  const remaining = team.filter(m => !attendees.includes(m.initials));
  const filtered  = query.trim()
    ? remaining.filter(m =>
        m.name.toLowerCase().includes(query.toLowerCase()) ||
        m.email.toLowerCase().includes(query.toLowerCase())
      )
    : remaining;
  if (remaining.length === 0) return null;
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${t.bd}`, borderRadius: 8, padding: '7px 10px', background: '#fafcfd' }}>
        <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='#b0c4cc' strokeWidth='2.2' strokeLinecap='round'><circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/></svg>
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
          placeholder={remaining.length + ' member' + (remaining.length !== 1 ? 's' : '') + ' available — type to search'}
          style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12, color: '#0f1e28', fontFamily: F, background: 'transparent' }}
        />
      </div>
      {open && filtered.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, background: t.panel, border: `1px solid ${t.bd}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(15,30,40,0.13)', marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
          {filtered.map(m => (
            <div key={m.initials}
              onMouseDown={() => { onAdd(m.initials); setQuery(''); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f4f7f9'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{m.initials}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0f1e28', lineHeight: 1.3 }}>{m.name}</div>
                <div style={{ fontSize: 10.5, color: '#9eb8c8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.email}</div>
              </div>
              {m.timezone && (
                <div style={{ marginLeft: 'auto', fontSize: 10, color: '#b0c8d4', flexShrink: 0 }}>
                  {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: m.timezone })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {open && filtered.length === 0 && query.trim() && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, background: t.panel, border: `1px solid ${t.bd}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(15,30,40,0.13)', marginTop: 4, padding: '12px', fontSize: 12, color: '#b0c8d4', fontStyle: 'italic', textAlign: 'center' }}>No members match</div>
      )}
    </div>
  );
}

// ── Event form modal ───────────────────────────────────────────────────────────
function EventForm({ event, onSave, onClose, isMobile, team, joinRoom }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [title, setTitle]       = useState(event?.title || '');
  const [type, setType]         = useState(event?.type  || 'meeting');
  const [allDay, setAllDay]     = useState(event?.allDay ?? false);
  const [dateStr, setDateStr]   = useState(() => localDateStr(event?.start || new Date()));
  const [startH, setStartH]     = useState(event?.start ? event.start.getHours() : 9);
  const [startM, setStartM]     = useState(event?.start ? event.start.getMinutes() : 0);
  const [endH, setEndH]         = useState(event?.end   ? event.end.getHours()   : 10);
  const [endM, setEndM]         = useState(event?.end   ? event.end.getMinutes() : 0);
  const [attendees, setAtt]     = useState(event?.attendees || []);
  // callType: 'none' | 'video' | 'quick'
  const [callType, setCallType] = useState(() => {
    // LOCKED (June 2026): every meeting is always a Team Room video call.
    if ((event?.type || type || 'meeting') === 'meeting') return 'video';
    if (event?.video) return 'video';
    return 'none';
  });
  // Auto-switch when type changes to/from meeting (new events only)
  React.useEffect(() => {
    // Meetings are always video (Team Room); any other type has no call.
    if (type === 'meeting') setCallType('video');
    else setCallType('none');
  }, [type]); // eslint-disable-line
  const video = callType === 'video'; // backward-compat alias used in save() + Meeting Link
  const [recur, setRecur]       = useState(event?.recur || 'Once');
  const [desc, setDesc]         = useState(event?.desc  || '');
  const [notify, setNotify]     = useState(false);
  const [timeMode, setTimeMode]  = useState('native'); // 'native' | 'dropdown'
  const [joining, setJoining]   = useState(false);
  const ts = getTypeStyle(type);

  // Duration picker: end time is derived from start + chosen duration.
  const curDur = Math.max(15, (endH * 60 + endM) - (startH * 60 + startM));
  function applyStart(h, m) {
    const dur = Math.max(15, (endH * 60 + endM) - (startH * 60 + startM));
    setStartH(h); setStartM(m);
    const endTot = Math.min(23 * 60 + 59, h * 60 + m + dur);
    setEndH(Math.floor(endTot / 60)); setEndM(endTot % 60);
  }
  function applyDuration(mins) {
    const endTot = Math.min(23 * 60 + 59, startH * 60 + startM + mins);
    setEndH(Math.floor(endTot / 60)); setEndM(endTot % 60);
  }

  function save() {
    if (!title.trim()) return;
    // Parse dateStr in local time (avoid UTC midnight shift)
    const [yr, mo, dy] = dateStr.split('-').map(Number);
    const s = new Date(yr, mo - 1, dy, startH, startM, 0, 0);
    const e = new Date(yr, mo - 1, dy, endH,   endM,   0, 0);
    onSave({ id: event?.id || ('ev' + Date.now()), title: title.trim(), type, allDay, start: s, end: e, attendees, video: type === 'meeting' ? true : (callType === 'video'), recur, desc, notify });
  }

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,30,40,0.45)', zIndex: 9000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16 };
  const modal   = { background: t.panel, borderRadius: isMobile ? '18px 18px 0 0' : 14, width: '100%', maxWidth: isMobile ? 560 : 420, maxHeight: isMobile ? 'calc(100dvh - 32px)' : 'none', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.22)', overflow: 'hidden', fontFamily: F };
  const SL      = { fontSize: 11, fontWeight: 600, color: '#5a7a8a', marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '.04em' };
  const SR      = { marginBottom: 14 };
  const SI      = { width: '100%', border: `1px solid ${t.bd}`, borderRadius: 8, padding: '8px 11px', fontSize: 13, fontFamily: F, color: '#0f1e28', outline: 'none' };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        <div style={{ background: ts.bg, padding: '14px 18px 13px', borderBottom: `1px solid ${t.bd}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: ts.solid, flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#0f1e28', flex: 1 }}>{event?.id ? 'Edit event' : 'New event'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9eb8c8', fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
        </div>
        <div style={{ padding: '16px 18px 18px', flex: 1, minHeight: 0, maxHeight: isMobile ? 'none' : '70vh', overflowY: 'auto' }}>
          <div style={SR}>
            <label style={SL}>Title</label>
            <input style={SI} value={title} onChange={e => setTitle(e.target.value)} placeholder='Event title' autoFocus />
          </div>
          <div style={{ ...SR, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={SL}>Type</label>
              <select style={SI} value={type} onChange={e => setType(e.target.value)}>
                {EVENT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div><label style={SL}>Recurrence</label>
              <select style={SI} value={recur} onChange={e => setRecur(e.target.value)}>
                {RECUR_OPTS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div style={{ ...SR, display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type='checkbox' id='allday' checked={allDay} onChange={e => setAllDay(e.target.checked)} />
            <label htmlFor='allday' style={{ fontSize: 12, color: '#314F5C', cursor: 'pointer' }}>All-day event</label>
          </div>
          <div style={SR}>
            <label style={SL}>Date</label>
            <input type='date' style={SI} value={dateStr} onChange={e => setDateStr(e.target.value)} />
          </div>
          {!allDay && (
            <div style={SR}>
              {/* ── Time mode toggle ── */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={SL}>Time</span>
                <div style={{ display: 'flex', background: 'rgba(49,79,92,0.07)', borderRadius: 7, padding: 2, gap: 2 }}>
                  {[['native','Clock'],['dropdown','Grid']].map(([mode, _label]) => (
                    <button key={mode} onClick={() => setTimeMode(mode)}
                      style={{ padding: '3px 10px', borderRadius: 5, border: 'none', cursor: 'pointer',
                        fontSize: '.65rem', fontWeight: 600, fontFamily: F, letterSpacing: '.02em',
                        background: timeMode === mode ? '#fff' : 'transparent',
                        color: timeMode === mode ? '#314F5C' : '#8aabba',
                        boxShadow: timeMode === mode ? '0 1px 3px rgba(49,79,92,0.12)' : 'none',
                        transition: 'all .15s' }}>
                      {mode === 'native' ? (
                        <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round'>
                          <circle cx='12' cy='12' r='10'/><polyline points='12 6 12 12 16 14'/>
                        </svg>
                      ) : (
                        <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round'>
                          <rect x='3' y='3' width='7' height='7'/><rect x='14' y='3' width='7' height='7'/>
                          <rect x='14' y='14' width='7' height='7'/><rect x='3' y='14' width='7' height='7'/>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              {/* ── Time inputs ── */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {timeMode === 'native' ? (
                  <>
                    <div>
                      <label style={SL}>Start</label>
                      <input type='time' style={SI}
                        value={String(startH).padStart(2,'0') + ':' + String(startM).padStart(2,'0')}
                        onChange={e => {
                          const [h, m] = (e.target.value || '09:00').split(':').map(Number);
                          applyStart(h, m);
                        }} />
                    </div>
                    <div>
                      <label style={SL}>Duration</label>
                      <select style={SI} value={curDur} onChange={e => applyDuration(+e.target.value)}>
                        {!DURATIONS.includes(curDur) && <option value={curDur}>{durLabel(curDur)}</option>}
                        {DURATIONS.map(m => <option key={m} value={m}>{durLabel(m)}</option>)}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label style={SL}>Start</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <select style={SI} value={startH} onChange={e => applyStart(+e.target.value, startM)}>
                          {HOURS.map(h => <option key={h} value={h}>{fmtTime(h)}</option>)}
                        </select>
                        <select style={SI} value={startM} onChange={e => applyStart(startH, +e.target.value)}>
                          {Array.from({length:12},(_,i)=>i*5).map(m => <option key={m} value={m}>{String(m).padStart(2,'0')}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label style={SL}>Duration</label>
                      <select style={SI} value={curDur} onChange={e => applyDuration(+e.target.value)}>
                        {!DURATIONS.includes(curDur) && <option value={curDur}>{durLabel(curDur)}</option>}
                        {DURATIONS.map(m => <option key={m} value={m}>{durLabel(m)}</option>)}
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          {attendees.length > 0 && (() => {
            const [yr, mo, dy] = dateStr.split('-').map(Number);
            const meetingDate = new Date(yr, mo - 1, dy, startH, startM, 0, 0);
            const myTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const fmtInTz = (tz) => meetingDate.toLocaleTimeString('en-US', {
              hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz,
            }) + ' ' + meetingDate.toLocaleDateString('en-US', {
              weekday: 'short', month: 'short', day: 'numeric', timeZone: tz,
            });
            return (
              <div style={{ ...SR, background: 'rgba(49,79,92,0.04)', border: '1px solid rgba(49,79,92,0.1)', borderRadius: 9, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#314F5C', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'><circle cx='12' cy='12' r='10'/><path d='M12 2a14.5 14.5 0 000 20M2 12h20'/></svg>
                  Meeting Time By Timezone
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                  <span style={{ fontSize: 11, color: '#5a7a8a', fontWeight: 600 }}>You</span>
                  <span style={{ fontSize: 11, color: '#0f1e28', fontWeight: 500 }}>{fmtInTz(myTz)}</span>
                </div>
                {team.filter(m => attendees.includes(m.initials)).map(m => (
                  <div key={m.initials} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, paddingTop: 4, borderTop: '1px solid rgba(49,79,92,0.07)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 16, height: 16, borderRadius: '50%', background: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7.5, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{m.initials}</div>
                      <span style={{ fontSize: 11, color: '#5a7a8a', fontWeight: 600 }}>{m.name}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {m.timezone ? (
                        <>
                          <div style={{ fontSize: 11, color: '#0f1e28', fontWeight: 500 }}>{fmtInTz(m.timezone)}</div>
                          <div style={{ fontSize: 9.5, color: '#b0c8d4' }}>{TIMEZONES.find(t => t.value === m.timezone)?.label || m.timezone}</div>
                        </>
                      ) : (
                        <div style={{ fontSize: 10, color: '#c0d0d8', fontStyle: 'italic' }}>No timezone set — set in Roles</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          <div style={SR}>
            <label style={SL}>Attendees</label>
            {attendees.length > 0 && (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
                {attendees.map(ini => {
                  const m = team.find(t => t.initials === ini) || { initials: ini, name: ini, color: '#6F8FA7' };
                  return (
                    <div key={ini} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px 4px 5px', borderRadius: 99, background: m.color + '18', border: '1px solid ' + m.color + '44' }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', background: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8.5, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{m.initials}</div>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#1a3040' }}>{m.name}</span>
                      <button onClick={() => setAtt(prev => prev.filter(x => x !== ini))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9eb8c8', fontSize: 14, lineHeight: 1, padding: '0 2px', display: 'flex', alignItems: 'center' }}>×</button>
                    </div>
                  );
                })}
              </div>
            )}
            <AttendeeSearch team={team} attendees={attendees} onAdd={ini => setAtt(prev => [...prev, ini])} />
            {team.length === 0 && (
              <div style={{ fontSize: 11, color: '#b0c8d4', fontStyle: 'italic' }}>No team members — add them in Roles & Access</div>
            )}
          </div>
          {/* Call — LOCKED: every meeting is a Team Room video call (June 2026) */}
          {type === 'meeting' && (
            <div style={SR}>
              <label style={SL}>Call</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'rgba(25,100,170,0.06)', border: '1px solid rgba(25,100,170,0.18)', borderRadius: 9, padding: '9px 12px' }}>
                <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='#1964aa' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.89L15 14'/><rect x='1' y='6' width='14' height='12' rx='3'/></svg>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#1964aa' }}>Team Room video call</div>
                  <div style={{ fontSize: 10.5, color: '#6a90b5', marginTop: 1 }}>Every meeting opens in Team Room — join from here or the event.</div>
                </div>
              </div>
            </div>
          )}
          <div style={{ ...SR, display: 'flex', gap: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#314F5C', cursor: 'pointer' }}>
              <input type='checkbox' checked={notify} onChange={e => setNotify(e.target.checked)} />Notify team
            </label>
          </div>
          {video && (
            <div style={{ ...SR, background: 'rgba(25,100,170,0.05)', border: '1px solid rgba(25,100,170,0.15)', borderRadius: 9, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#1964aa', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Meeting Link</div>
              {event?.room_url ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <a href={event.room_url} target='_blank' rel='noopener noreferrer'
                    style={{ flex: 1, fontSize: 11.5, color: '#1964aa', fontWeight: 500, wordBreak: 'break-all', textDecoration: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                    onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
                    {event.room_url}
                  </a>
                  <button onClick={() => navigator.clipboard.writeText(event.room_url)}
                    title='Copy link'
                    style={{ flexShrink: 0, background: 'rgba(25,100,170,0.1)', border: '1px solid rgba(25,100,170,0.2)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: '#1964aa', fontFamily: F }}>
                    <svg width='10' height='10' viewBox='0 0 14 14' fill='none'><rect x='1' y='4' width='8' height='9' rx='1.5' stroke='currentColor' strokeWidth='1.3'/><path d='M4 4V3a1 1 0 011-1h5a1 1 0 011 1v7a1 1 0 01-1 1h-1' stroke='currentColor' strokeWidth='1.3'/></svg>
                    Copy
                  </button>
                </div>
              ) : (
                <div style={{ fontSize: 11, color: '#6a90b5', fontStyle: 'italic' }}>
                  Link will be created when you click Join for the first time.
                </div>
              )}
            </div>
          )}
          <div style={SR}>
            <label style={SL}>Notes</label>
            <textarea style={{ ...SI, resize: 'none', minHeight: 60 }} value={desc} onChange={e => setDesc(e.target.value)} placeholder='Optional description…' />
          </div>
        </div>
        <div style={{ padding: isMobile ? '12px 18px calc(12px + env(safe-area-inset-bottom))' : '12px 18px', flexShrink: 0, borderTop: `1px solid ${t.bd}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {event?.id && callType === 'video' && (
            <button onClick={async () => { setJoining(true); await joinRoom(event); setJoining(false); }} disabled={joining}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px',
                background: joining ? '#e4edf3' : 'linear-gradient(135deg,#1964aa 0%,#0f4a87 100%)',
                color: joining ? '#9eb8c8' : '#fff', border: 'none', borderRadius: 8,
                fontSize: 12, fontWeight: 600, cursor: joining ? 'default' : 'pointer', fontFamily: F,
                boxShadow: joining ? 'none' : '0 1px 0 rgba(0,0,0,0.15) inset, 0 2px 8px rgba(25,100,170,0.35)' }}>
              <svg width='10' height='10' viewBox='0 0 14 14' fill='none'><path d='M1 4h7v6H1V4z' stroke='currentColor' strokeWidth='1.4' strokeLinejoin='round'/><path d='M8 5.5l5-2v7l-5-2V5.5z' stroke='currentColor' strokeWidth='1.4' strokeLinejoin='round'/></svg>
              {joining ? 'Joining…' : 'Join'}
            </button>
          )}
          {callType === 'quick' && (
            <button onClick={() => {
              window.dispatchEvent(new CustomEvent('sanoire:startcall', {
                detail: { type: 'calendar', id: event?.id || 'quick', name: title.trim() || 'Quick Call', roomUrl: null }
              }));
              onClose();
            }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px',
                background: 'linear-gradient(135deg,#2da06a 0%,#1e8a57 100%)',
                color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: F,
                boxShadow: '0 1px 0 rgba(0,0,0,0.15) inset, 0 2px 8px rgba(45,160,106,0.35)' }}>
              <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round'><path d='M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .84h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.28a16 16 0 006.29 6.29l1.46-1.46a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 15.31z'/></svg>
              Quick Call
            </button>
          )}
          <button onClick={onClose} style={{ padding: '7px 14px', background: 'transparent', border: `1px solid ${t.bd}`, borderRadius: 8, fontSize: 12, color: '#5a7a8a', cursor: 'pointer', fontFamily: F }}>Cancel</button>
          <button onClick={save} disabled={!title.trim()} style={{ padding: '7px 16px', background: title.trim() ? '#314F5C' : '#e4edf3', color: title.trim() ? '#E0F5FC' : '#9eb8c8', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: title.trim() ? 'pointer' : 'default', fontFamily: F }}>Save event</button>
        </div>
      </div>
    </div>
  );
}

// ── Planner assignee search picker ──────────────────────────────────────────────────────────────────────
// ââ Event peek ââ read-only sheet shown on event tap (June 2026) âââââââââââââ
// Tap = glance, not edit. Bottom sheet on mobile, centered card on desktop:
// title, time, per-attendee local times, Join when video. Edit opens the full
// EventForm; Delete confirms here (it used to sit beside Join in the form).
function EventPeek({ event, team, isMobile, onClose, onEdit, onDelete, joinRoom, ensureRoom, inviteLink }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [joining, setJoining] = useState(false);
  // MEETING-PRESENCE (June 2026) — who is already in this meeting's Team Room.
  const [present, setPresent] = useState([]);
  const [copied, setCopied] = useState(false);
  const copyInvite = async () => {
    if (!event?.video) return;
    let url = event.room_url;
    if (!url && ensureRoom) url = await ensureRoom(event);
    const link = inviteLink ? inviteLink(url) : '';
    if (!link) { setCopied('err'); setTimeout(() => setCopied(false), 2000); return; }
    try { await navigator.clipboard.writeText(link); } catch (_e) {}
    setCopied(true); setTimeout(() => setCopied(false), 2200);
  };
  useEffect(() => {
    if (!event?.room_url) { setPresent([]); return undefined; }
    let alive = true;
    const check = async () => {
      try {
        const cutoff = new Date(Date.now() - 3 * 3600 * 1000).toISOString();
        const { data } = await supabase
          .from('call_video_sessions')
          .select('participants, started_by, status, started_at')
          .eq('room_url', event.room_url).eq('status', 'active')
          .gte('started_at', cutoff);
        if (!alive) return;
        const names = new Set();
        for (const ses of (data || [])) {
          (Array.isArray(ses.participants) ? ses.participants : []).forEach(n => n && names.add(n));
          if (ses.started_by && ses.started_by !== 'Overlay') names.add(ses.started_by);
        }
        setPresent(Array.from(names));
      } catch (_e) { if (alive) setPresent([]); }
    };
    check();
    const iv = setInterval(check, 12000);
    return () => { alive = false; clearInterval(iv); };
  }, [event?.room_url]);
  if (!event) return null;
  const ts = getTypeStyle(event.type);
  const s = new Date(event.start), e = new Date(event.end);
  const dayLabel = s.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const dateLine = event.allDay
    ? dayLabel + ' · All day'
    : dayLabel + ' · ' + fmtTime(s.getHours(), s.getMinutes()) + ' – ' + fmtTime(e.getHours(), e.getMinutes());
  const members = (event.attendees || []).map(ini => team.find(m => m.initials === ini)).filter(Boolean);
  const tzRows = members.filter(m => m.timezone).map(m => {
    let local = null;
    try { local = s.toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit', timeZone: m.timezone }); } catch (_e) {}
    return { m, local };
  }).filter(r => r.local);
  const wrap = { position: 'fixed', inset: 0, background: 'rgba(15,30,40,0.45)', zIndex: 9000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16 };
  const sheet = isMobile
    ? { background: '#fff', borderRadius: '18px 18px 0 0', width: '100%', maxWidth: 560, padding: '10px 18px calc(18px + env(safe-area-inset-bottom))', fontFamily: F, boxShadow: '0 -12px 40px rgba(0,0,0,0.22)' }
    : { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 380, padding: '16px 20px 18px', fontFamily: F, boxShadow: '0 24px 64px rgba(0,0,0,0.22)' };
  return (
    <div style={wrap} onClick={ev2 => { if (ev2.target === ev2.currentTarget) onClose(); }}>
      <div style={sheet}>
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: '#d8e4ea', margin: '0 auto 14px' }} />}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: ts.solid, marginTop: 7, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f1e28', lineHeight: 1.3 }}>{event.title}</div>
            <div style={{ fontSize: 12.5, color: '#5a7a8a', marginTop: 3 }}>{dateLine}</div>
            <div style={{ fontSize: 11.5, color: '#9eb8c8', marginTop: 2, textTransform: 'capitalize' }}>{event.type || 'meeting'}{event.recur && event.recur !== 'Once' ? ' · ' + event.recur : ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9eb8c8', fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
        </div>
        {tzRows.length > 0 && (
          <div style={{ marginTop: 14, background: '#f6f9fb', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.08em', color: '#6F8FA7', marginBottom: 6 }}>MEETING TIME BY TIMEZONE</div>
            {tzRows.map(({ m, local }) => (
              <div key={m.initials} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, color: '#0f1e28', padding: '2px 0' }}>
                <span style={{ color: '#5a7a8a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                <span style={{ flexShrink: 0 }}>{local}</span>
              </div>
            ))}
          </div>
        )}
        {members.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
            {members.map(m => (
              <span key={m.initials} title={m.name} style={{ width: 26, height: 26, borderRadius: '50%', background: m.color || '#314F5C', color: m.text || '#fff', fontSize: 10.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{m.initials}</span>
            ))}
            <span style={{ fontSize: 12, color: '#5a7a8a' }}>{members.map(m => m.name).join(', ')}</span>
          </div>
        )}
        {event.desc ? <div style={{ fontSize: 12.5, color: '#3a5060', marginTop: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{event.desc}</div> : null}
        {event.video && (
          <button onClick={copyInvite}
            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', marginTop: 14, padding: '9px 12px', background: copied === true ? 'rgba(45,160,106,0.08)' : '#f6f9fb', border: `1px solid ${copied === true ? 'rgba(45,160,106,0.3)' : t.bd}`, borderRadius: 9, cursor: 'pointer', fontFamily: F, textAlign: 'left' }}
            title='Copy a link guests can open to join the “are you ready” room'>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke={copied === true ? '#1c7a4a' : '#6F8FA7'} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              {copied === true
                ? <polyline points='20 6 9 17 4 12'/>
                : <><path d='M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71'/><path d='M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71'/></>}
            </svg>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: copied === true ? '#1c7a4a' : '#314F5C' }}>
                {copied === true ? 'Invite link copied' : copied === 'err' ? 'Could not create link' : 'Copy invite link'}
              </div>
              <div style={{ fontSize: 10.5, color: '#9eb8c8', marginTop: 1 }}>Opens the “are you ready” room for whoever you share it with.</div>
            </div>
          </button>
        )}
        {event.video && present.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, padding: '9px 12px', background: 'rgba(45,160,106,0.08)', border: '1px solid rgba(45,160,106,0.22)', borderRadius: 9 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2da06a', boxShadow: '0 0 0 3px rgba(45,160,106,0.16)', flexShrink: 0 }} />
            <span style={{ fontSize: 12.5, color: '#1c7a4a', fontWeight: 600 }}>
              {present.length === 1 ? `${present[0]} is in the meeting` : `${present.length} people are in the meeting`}
            </span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: present.length > 0 ? 10 : 16 }}>
          {event.video && (
            <button onClick={async () => { setJoining(true); try { await joinRoom(event); } catch (_e) {} setJoining(false); }} disabled={joining}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 0', background: joining ? '#e4edf3' : (present.length > 0 ? '#2da06a' : '#1964aa'), color: joining ? '#9eb8c8' : '#fff', border: 'none', borderRadius: 10, fontSize: 13.5, fontWeight: 600, cursor: joining ? 'default' : 'pointer', fontFamily: F }}>
              <svg width='12' height='12' viewBox='0 0 14 14' fill='none'><path d='M1 4h7v6H1V4z' stroke='currentColor' strokeWidth='1.4' strokeLinejoin='round'/><path d='M8 5.5l5-2v7l-5-2V5.5z' stroke='currentColor' strokeWidth='1.4' strokeLinejoin='round'/></svg>
              {joining ? 'Joining…' : (present.length > 0 ? 'Join now' : 'Join')}
            </button>
          )}
          <button onClick={onEdit}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 0', background: '#fff', color: '#314F5C', border: `1px solid ${t.bd}`, borderRadius: 10, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: F }}>
            Edit
          </button>
          <button onClick={onDelete} title='Delete event'
            style={{ width: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '11px 0', background: '#fff', color: '#c0392b', border: '1px solid #f0d4d4', borderRadius: 10, cursor: 'pointer', fontFamily: F }}>
            <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'><polyline points='3 6 5 6 21 6'/><path d='M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2'/></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function PlannerAssigneeSearch({ team, assignees, onAdd, onRemove }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [query, setQuery] = React.useState('');
  const [open,  setOpen]  = React.useState(false);
  const remaining = team.filter(m => !assignees.includes(m.initials));
  const filtered  = query.trim()
    ? remaining.filter(m => m.name.toLowerCase().includes(query.toLowerCase()) || (m.email || '').toLowerCase().includes(query.toLowerCase()))
    : remaining;
  return (
    <div>
      {assignees.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
          {assignees.map(ini => {
            const m = team.find(t => t.initials === ini) || { initials: ini, name: ini, color: '#314F5C' };
            return (
              <div key={ini} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px 4px 5px', borderRadius: 99, background: m.color + '18', border: '1px solid ' + m.color + '44' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8.5, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{m.initials}</div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#1a3040' }}>{m.name}</span>
                <button onClick={() => onRemove(ini)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9eb8c8', fontSize: 14, lineHeight: 1, padding: '0 2px', display: 'flex', alignItems: 'center' }}>×</button>
              </div>
            );
          })}
        </div>
      )}
      {remaining.length > 0 && (
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${t.bd}`, borderRadius: 8, padding: '7px 10px', background: '#fafcfd' }}>
            <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='#b0c4cc' strokeWidth='2.2' strokeLinecap='round'><circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/></svg>
            <input value={query}
              onChange={e => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 160)}
              placeholder={remaining.length + ' member' + (remaining.length !== 1 ? 's' : '') + ' available — type to search'}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12, color: '#0f1e28', fontFamily: F, background: 'transparent' }} />
          </div>
          {open && filtered.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, background: t.panel, border: `1px solid ${t.bd}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(15,30,40,0.13)', marginTop: 4, maxHeight: 200, overflowY: 'auto' }}>
              {filtered.map(m => (
                <div key={m.initials} onMouseDown={() => { onAdd(m.initials); setQuery(''); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f4f7f9'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{m.initials}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0f1e28', lineHeight: 1.3 }}>{m.name}</div>
                    <div style={{ fontSize: 10.5, color: '#9eb8c8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {open && filtered.length === 0 && query.trim() && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999, background: t.panel, border: `1px solid ${t.bd}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(15,30,40,0.13)', marginTop: 4, padding: '12px', fontSize: 12, color: '#b0c8d4', fontStyle: 'italic', textAlign: 'center' }}>No members match</div>
          )}
        </div>
      )}
      {team.length === 0 && <div style={{ fontSize: 11, color: '#b0c8d4', fontStyle: 'italic' }}>No team members — add in Roles & Access</div>}
    </div>
  );
}

// ── Task form modal ────────────────────────────────────────────────────────────
function TaskForm({ task, onSave, onClose, team, boards, onOpenBoard }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [title,    setTitle]    = useState(task?.title    || '');
  const [status,   setStatus]   = useState(task?.status   || 'todo');
  const [priority, setPriority] = useState(task?.priority || 'p2');
  const [dueDate,  setDueDate]  = useState(task?.due_date || '');
  const [tags,     setTags]     = useState(task?.tags     || []);
  const [assignees, setAssignees] = useState(task?.assignees || (task?.assignee ? [task.assignee] : []));
  const [desc,     setDesc]     = useState(task?.description || '');
  const [boardId,  setBoardId]  = useState(task?.studio_board_id || '');

  function save() {
    if (!title.trim()) return;
    onSave({ id: task?.id, title: title.trim(), status, priority, due_date: dueDate || null, tags, assignees, description: desc, studio_board_id: boardId || null });
  }

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,30,40,0.45)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
  const modal   = { background: t.panel, borderRadius: 14, width: '100%', maxWidth: 400, boxShadow: '0 24px 64px rgba(0,0,0,0.22)', overflow: 'hidden', fontFamily: F };
  const SL      = { fontSize: 11, fontWeight: 600, color: '#5a7a8a', marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '.04em' };
  const SR      = { marginBottom: 14 };
  const SI      = { width: '100%', border: `1px solid ${t.bd}`, borderRadius: 8, padding: '8px 11px', fontSize: 13, fontFamily: F, color: '#0f1e28', outline: 'none' };
  const allTags = Object.keys(TAG_MAP);

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        <div style={{ padding: '14px 18px 13px', borderBottom: `1px solid ${t.bd}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#0f1e28', flex: 1 }}>{task?.id ? 'Edit task' : 'New task'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9eb8c8', fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
        </div>
        <div style={{ padding: '16px 18px 18px', maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={SR}>
            <label style={SL}>Title</label>
            <input style={SI} value={title} onChange={e => setTitle(e.target.value)} placeholder='Task title' autoFocus />
          </div>
          <div style={{ ...SR, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={SL}>Status</label>
              <select style={SI} value={status} onChange={e => setStatus(e.target.value)}>
                {COL_CFG.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div><label style={SL}>Priority</label>
              <select style={SI} value={priority} onChange={e => setPriority(e.target.value)}>
                <option value='p1'>P1 Critical</option>
                <option value='p2'>P2 High</option>
                <option value='p3'>P3 Normal</option>
              </select>
            </div>
          </div>
          <div style={SR}>
            <label style={SL}>Due date</label>
            <input type='date' style={SI} value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </div>
          <div style={SR}>
            <label style={SL}>Assignee</label>
            <PlannerAssigneeSearch
              team={team}
              assignees={assignees}
              onAdd={ini => setAssignees(prev => [...prev, ini])}
              onRemove={ini => setAssignees(prev => prev.filter(x => x !== ini))}
            />
          </div>
          <div style={SR}>
            <label style={SL}>Tags</label>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {allTags.map(tag => {
                const sel = tags.includes(tag);
                const ts2 = TAG_MAP[tag];
                return (
                  <button key={tag} onClick={() => setTags(prev => sel ? prev.filter(x => x !== tag) : [...prev, tag])}
                    style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid ' + (sel ? ts2.color : '#dde8ed'), background: sel ? ts2.bg : '#fff', color: sel ? ts2.color : '#7a9aaa', fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: F }}>
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={SR}>
            <label style={SL}>Description</label>
            <textarea style={{ ...SI, resize: 'none', minHeight: 60 }} value={desc} onChange={e => setDesc(e.target.value)} placeholder='Optional notes…' />
          </div>
          {/* Studio board link */}
          {boards && boards.length > 0 && (
            <div style={SR}>
              <label style={SL}>Studio board</label>
              <div style={{ display:'flex', gap:8 }}>
                <select style={{ ...SI, flex:1 }} value={boardId} onChange={e => setBoardId(e.target.value)}>
                  <option value=''>No board linked</option>
                  {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                {boardId && onOpenBoard && (
                  <button type='button' onClick={() => onOpenBoard(boardId)}
                    style={{ padding:'8px 12px', borderRadius:8, border:`1px solid ${t.bd}`, background:t.rowbg, fontSize:12, cursor:'pointer', fontFamily:F, color:t.accent, fontWeight:600, whiteSpace:'nowrap', flexShrink:0 }}>
                    Open ↗
                  </button>
                )}
              </div>
              {!boardId && onOpenBoard && (
                <button type='button' onClick={() => onOpenBoard('new', task?.id, title.trim())}
                  style={{ marginTop:5, fontSize:11, color:t.mut, background:'none', border:'none', cursor:'pointer', fontFamily:F, padding:0 }}>
                  + Create board for this task
                </button>
              )}
            </div>
          )}
        </div>
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${t.bd}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '7px 14px', background: 'transparent', border: `1px solid ${t.bd}`, borderRadius: 8, fontSize: 12, color: '#5a7a8a', cursor: 'pointer', fontFamily: F }}>Cancel</button>
          <button onClick={save} disabled={!title.trim()} style={{ padding: '7px 16px', background: title.trim() ? '#314F5C' : '#e4edf3', color: title.trim() ? '#E0F5FC' : '#9eb8c8', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: title.trim() ? 'pointer' : 'default', fontFamily: F }}>Save task</button>
        </div>
      </div>
    </div>
  );
}

// ── Month grid ─────────────────────────────────────────────────────────────────
function MonthView({ events, tasks, weekStart, onDayClick, onEventClick }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const today = new Date();
  const firstOfMonth = new Date(weekStart.getFullYear(), weekStart.getMonth(), 1);
  const gridStart = startOfWeek(firstOfMonth);
  const days = Array.from({ length: 35 }, (_, i) => addDays(gridStart, i));
  const DOW = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', borderBottom: `1px solid ${GOLD_HAIR_LG}`, background: PAPER_LG }}>
        {DOW.map((d, i) => (
          <div key={d} style={{ padding: '8px 0', textAlign: 'center', fontSize: 10.5, fontWeight: 600, color: i >= 5 ? '#c2b89e' : '#9eb8c8', textTransform: 'uppercase', letterSpacing: '.06em' }}>{d}</div>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gridAutoRows: 'minmax(84px, 1fr)', gap: '1px', background: GOLD_HAIR_LG }}>
        {days.map((day, i) => {
          const isToday  = sameDay(day, today);
          const isOther  = day.getMonth() !== firstOfMonth.getMonth();
          const isWknd   = i % 7 >= 5;
          const dayEvs   = events.filter(ev => !ev.allDay && sameDay(new Date(ev.start), day));
          const dayAll   = events.filter(ev => ev.allDay && localDateStr(ev.start) <= localDateStr(day) && localDateStr(ev.end) >= localDateStr(day));
          const dayTasks = tasks.filter(t => t.due_date && sameDay(new Date(t.due_date + 'T00:00:00'), day));
          return (
            <div key={i} onClick={() => onDayClick(day)}
              style={{ background: isToday ? '#fffdf8' : isOther ? '#f3efe6' : isWknd ? PAPER_DEEP_LG : PAPER_LG, padding: '6px 7px', cursor: 'pointer', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', background: isToday ? INK_LG : 'transparent', marginBottom: 4 }}>
                <span style={{ fontFamily: SERIF_LG, fontSize: 13, color: isToday ? '#f7f4ee' : isOther ? '#cfc6ae' : isWknd ? '#b8ac90' : INK_LG, fontWeight: isToday ? 700 : 400, lineHeight: 1 }}>{day.getDate()}</span>
              </div>
              {dayAll.slice(0,1).map(ev => {
                const ts = getTypeStyle(ev.type);
                return <div key={ev.id} onClick={e => { e.stopPropagation(); onEventClick(ev); }} style={{ fontSize: 9.5, fontWeight: 600, padding: '2px 5px', borderRadius: 4, background: ts.bg, color: ts.color, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', borderLeft: '2px solid ' + ts.solid }}>{ev.title}</div>;
              })}
              {dayEvs.slice(0,1).map(ev => {
                const ts = getTypeStyle(ev.type);
                return <div key={ev.id} onClick={e => { e.stopPropagation(); onEventClick(ev); }} style={{ fontSize: 9.5, fontWeight: 600, padding: '2px 5px', borderRadius: 4, background: ts.bg, color: ts.color, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', borderLeft: '2px solid ' + ts.solid }}>{ev.title}</div>;
              })}
              {dayTasks.slice(0,1).map(t => {
                const ps = PRI_STYLE[t.priority] || PRI_STYLE.p3;
                return <div key={t.id} style={{ fontSize: 9.5, fontWeight: 500, padding: '2px 5px', borderRadius: 4, background: ps.bg, color: ps.color, border: '1px solid ' + ps.border, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: ps.dot, flexShrink: 0 }} />{t.title}
                </div>;
              })}
              {(dayEvs.length + dayAll.length + dayTasks.length) > 2 && (
                <div style={{ fontSize: 9, fontWeight: 700, color: '#6F8FA7',
                  background: 'rgba(49,79,92,0.07)', borderRadius: 4,
                  padding: '1px 5px', display: 'inline-block', marginTop: 1 }}>
                  +{dayEvs.length + dayAll.length + dayTasks.length - 2} more
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Week view ──────────────────────────────────────────────────────────────────
function WeekView({ events, tasks, weekStart, onEventClick, onSlotClick }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const today   = new Date();
  const now     = useCurrentTime();
  const days    = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const DOW     = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const allDayEvs = events.filter(ev => ev.allDay);
  const scrollRef = useRef(null);

  // Scroll to current time with 1hr context above
  useEffect(() => {
    if (scrollRef.current) {
      const h = now.getHours();
      const m = now.getMinutes();
      const firstHour = HOURS[0];
      const exactPx = (h - firstHour + m / 60) * ROW_H;
      scrollRef.current.scrollTop = Math.max(0, exactPx - ROW_H);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Current-time position: fraction of hour
  const nowH   = now.getHours();
  const nowM   = now.getMinutes();
  const firstH = HOURS[0];
  const lastH  = HOURS[HOURS.length - 1] + 1;
  const inRange = nowH >= firstH && nowH < lastH;
  const nowPct  = inRange ? ((nowH - firstH) + nowM / 60) / (lastH - firstH) : null;
  const todayIdx = days.findIndex(d => sameDay(d, today));

  return (
    <div style={{ flex: 1, overflowY: 'auto', position: 'relative' }} ref={scrollRef}>
      {/* Unified day header — dates + all-day events merged, no separate strip */}
      <div style={{ display: 'grid', gridTemplateColumns: '48px repeat(7,1fr)', position: 'sticky', top: 0, background: t.panel, zIndex: 6, borderBottom: '2px solid #e8eef2' }}>
        {/* Gutter: "all day" label anchored to bottom when events exist */}
        <div style={{ borderRight: `1px solid ${t.bd}`, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'flex-end', padding: '0 7px 6px' }}>
          {allDayEvs.length > 0 && (
            <span style={{ fontSize: 8.5, color: '#c0d0d8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', lineHeight: 1 }}>all day</span>
          )}
        </div>
        {days.map((day, i) => {
          const isTod  = sameDay(day, today);
          const isWknd = i >= 5;
          const dayAll = allDayEvs.filter(ev => localDateStr(new Date(ev.start)) <= localDateStr(day) && localDateStr(new Date(ev.end)) >= localDateStr(day));
          return (
            <div key={i} style={{ borderRight: i < 6 ? `1px solid ${t.bd}` : 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 10, paddingBottom: dayAll.length > 0 ? 5 : 9, background: isTod ? 'rgba(49,79,92,0.035)' : 'transparent', borderBottom: isTod ? '2px solid #314F5C' : '2px solid transparent', marginBottom: -2 }}>
              {/* Day name */}
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: isTod ? '#314F5C' : isWknd ? '#c0ccd4' : '#9eb8c8', marginBottom: 3 }}>{DOW[i]}</div>
              {/* Date circle */}
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: isTod ? '#314F5C' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: dayAll.length > 0 ? 6 : 0 }}>
                <span style={{ fontSize: 15, fontWeight: isTod ? 700 : 300, color: isTod ? '#E0F5FC' : isWknd ? '#b0c0c8' : '#3a5060', lineHeight: 1 }}>{day.getDate()}</span>
              </div>
              {/* All-day event pills — compact, flush under date */}
              {dayAll.length > 0 && (
                <div style={{ width: '100%', padding: '0 3px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {dayAll.map(ev => {
                    const ts = getTypeStyle(ev.type);
                    return (
                      <div key={ev.id} onClick={() => onEventClick(ev)}
                        style={{ fontSize: 9.5, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: ts.bg, color: ts.color, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderLeft: '2.5px solid ' + ts.solid }}>
                        {ev.title}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Time grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '48px repeat(7,1fr)', position: 'relative' }}>
        {/* Current time line — spans all day columns */}
        {nowPct !== null && todayIdx >= 0 && (() => {
          const topPx = nowPct * HOURS.length * ROW_H;
          const _leftPx = 48 + todayIdx * ((100 - 48 / 10) / 7);
          return (
            <div style={{ position: 'absolute', top: topPx, left: 48, right: 0, zIndex: 8, pointerEvents: 'none', display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
              {days.map((day, i) => {
                const isTod = sameDay(day, today);
                return (
                  <div key={i} style={{ position: 'relative', height: 0, borderTop: isTod ? '2px solid #e05c5c' : '0px solid transparent' }}>
                    {isTod && <div style={{ position: 'absolute', left: -5, top: -5, width: 9, height: 9, borderRadius: '50%', background: '#e05c5c' }} />}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {HOURS.map(h => (
          <React.Fragment key={h}>
            {/* Time label — single line, clean */}
            <div style={{ height: ROW_H, borderRight: `1px solid ${t.bd}`, borderBottom: '1px solid #f2f4f6', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', paddingRight: 8, paddingTop: 5, flexShrink: 0 }}>
              <span style={{ fontSize: 10, color: '#b8c8d0', fontWeight: 500, whiteSpace: 'nowrap', lineHeight: 1 }}>{fmtTime(h)}</span>
            </div>
            {days.map((day, di) => {
              const isWknd = di >= 5;
              const isTod  = sameDay(day, today);
              const slotEvs   = events.filter(ev => !ev.allDay && sameDay(new Date(ev.start), day) && new Date(ev.start).getHours() === h);
              const slotTasks = tasks.filter(t => t.due_date && sameDay(new Date(t.due_date + 'T00:00:00'), day) && h === 9);
              return (
                <div key={di} onClick={() => onSlotClick(day, h)}
                  style={{ height: ROW_H, borderRight: di < 6 ? `1px solid ${t.bd}` : 'none', borderBottom: '1px solid #f2f4f6', background: isTod ? 'rgba(49,79,92,0.025)' : isWknd ? 'rgba(0,0,0,0.018)' : 'transparent', position: 'relative', cursor: 'pointer' }}
                  onMouseEnter={e => { if (!isTod && !isWknd) e.currentTarget.style.background = 'rgba(49,79,92,0.02)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isTod ? 'rgba(49,79,92,0.025)' : isWknd ? 'rgba(0,0,0,0.018)' : 'transparent'; }}>
                  {slotEvs.map((ev, ei) => {
                    const ts  = getTypeStyle(ev.type);
                    const dur = Math.max(0.5, (new Date(ev.end) - new Date(ev.start)) / 3600000);
                    return (
                      <div key={ev.id} onClick={e => { e.stopPropagation(); onEventClick(ev); }}
                        style={{ position: 'absolute', left: 2 + ei * 5, right: 2, top: 3 + (new Date(ev.start).getMinutes() / 60) * ROW_H, height: Math.min(dur * ROW_H - 5, ROW_H * 3 - 5), borderRadius: 7, background: ts.bg, borderLeft: '3px solid ' + ts.solid, padding: '4px 7px', cursor: 'pointer', overflow: 'hidden', zIndex: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: ts.color, lineHeight: 1.3, flex: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ev.title}</div>
                          {ev.video && <svg width='9' height='9' viewBox='0 0 14 14' fill='none' style={{ flexShrink: 0, opacity: 0.8 }}><path d='M1 4h7v6H1V4z' stroke={ts.color} strokeWidth='1.5' strokeLinejoin='round'/><path d='M8 5.5l5-2v7l-5-2V5.5z' stroke={ts.color} strokeWidth='1.5' strokeLinejoin='round'/></svg>}
                        </div>
                        {dur >= 0.75 && <div style={{ fontSize: 9.5, color: ts.color, opacity: 0.75, whiteSpace: 'nowrap' }}>{fmtTime(new Date(ev.start).getHours(), new Date(ev.start).getMinutes())}</div>}
                      </div>
                    );
                  })}
                  {slotTasks.map((t, ti) => {
                    const ps = PRI_STYLE[t.priority] || PRI_STYLE.p3;
                    return (
                      <div key={t.id} style={{ position: 'absolute', left: 2, right: 2, top: 24 + ti * 22, height: 19, borderRadius: 5, background: ps.bg, border: '1px solid ' + ps.border, padding: '1px 6px', display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden', zIndex: 2 }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: ps.dot, flexShrink: 0 }} />
                        <span style={{ fontSize: 9.5, fontWeight: 600, color: ps.color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ── Mobile month view ── orientation grid + day panel (June 2026, Ledger) ────
// Month cells are too small on a phone for text pills ("Sa…"). The grid shows
// serif numerals + dots only (gold = event, blue = task due); tapping a day
// fills the panel below with that day's events AND due tasks — the Ops half
// of the approved Ledger + Ops mix.
function MobileMonthView({ events, tasks, weekStart, onEventClick, onSlotClick }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const today = new Date();
  const firstOfMonth = new Date(weekStart.getFullYear(), weekStart.getMonth(), 1);
  const gridStart = startOfWeek(firstOfMonth);
  const days = Array.from({ length: 35 }, (_, i) => addDays(gridStart, i));
  const sameMonth = d => d.getMonth() === firstOfMonth.getMonth() && d.getFullYear() === firstOfMonth.getFullYear();
  const [sel, setSel] = useState(() => sameMonth(new Date()) ? new Date() : firstOfMonth);
  useEffect(() => {
    const t0 = new Date();
    setSel(sameMonth(t0) ? t0 : firstOfMonth);
  }, [+firstOfMonth]); // eslint-disable-line react-hooks/exhaustive-deps
  const DOW = ['M','T','W','T','F','S','S'];
  const evOn = d => events.filter(ev => ev.allDay
    ? (localDateStr(new Date(ev.start)) <= localDateStr(d) && localDateStr(new Date(ev.end)) >= localDateStr(d))
    : sameDay(new Date(ev.start), d));
  const tkOn = d => tasks.filter(tk => tk.status !== 'done' && tk.due_date && sameDay(new Date(tk.due_date + 'T00:00:00'), d));
  const selEvs = evOn(sel).sort((a, b) => new Date(a.start) - new Date(b.start));
  const selTks = tkOn(sel);
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, background: PAPER_LG }}>
      <div style={{ flexShrink: 0, borderBottom: `1px solid ${GOLD_HAIR_LG}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
          {DOW.map((d, i) => (
            <div key={i} style={{ textAlign: 'center', padding: '7px 0 3px', fontSize: 9, fontWeight: 700, letterSpacing: '.08em', color: i >= 5 ? '#c2b89e' : '#9eb8c8' }}>{d}</div>
          ))}
          {days.map((day, i) => {
            const isTod = sameDay(day, today);
            const isSel = sameDay(day, sel);
            const isOther = !sameMonth(day);
            const nEv = evOn(day).length, nTk = tkOn(day).length;
            return (
              <button key={i} onClick={() => setSel(day)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: F, padding: '3px 0 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <span style={{ width: 28, height: 28, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: isSel ? INK_LG : isTod ? 'rgba(159,126,79,0.16)' : 'transparent' }}>
                  <span style={{ fontFamily: SERIF_LG, fontSize: 14, color: isSel ? '#f7f4ee' : isOther ? '#cfc6ae' : isTod ? INK_LG : '#3a5060', fontWeight: isSel || isTod ? 700 : 400, lineHeight: 1 }}>{day.getDate()}</span>
                </span>
                <span style={{ display: 'flex', gap: 2, height: 4 }}>
                  {nEv > 0 && <span style={{ width: 4, height: 4, borderRadius: '50%', background: GOLD_LG }} />}
                  {nTk > 0 && <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#6F8FA7' }} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px 16px' }}>
        <div style={{ fontFamily: SERIF_LG, fontSize: 10, letterSpacing: '.14em', color: GOLD_LG, textTransform: 'uppercase' }}>
          {sel.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        {selEvs.length === 0 && selTks.length === 0 && (
          <button onClick={() => onSlotClick(sel, 9)} style={{ border: 'none', background: 'transparent', padding: 0, marginTop: 10, cursor: 'pointer', textAlign: 'left' }}>
            <span style={{ fontFamily: SERIF_LG, fontStyle: 'italic', fontSize: 13.5, color: '#b0a890' }}>Nothing planned — tap to add something.</span>
          </button>
        )}
        {selEvs.map(ev => {
          const ts2 = getTypeStyle(ev.type);
          const s = new Date(ev.start), e2 = new Date(ev.end);
          return (
            <div key={ev.id} onClick={() => onEventClick(ev)}
              style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: '#fff', borderRadius: 10, borderLeft: '3px solid ' + ts2.solid, padding: '10px 12px', marginTop: 9, cursor: 'pointer', boxShadow: '0 1px 3px rgba(26,46,56,0.06)' }}>
              <div style={{ fontFamily: SERIF_LG, fontSize: 11, color: '#9eb8c8', flexShrink: 0, marginTop: 2, minWidth: 48 }}>
                {ev.allDay ? 'All day' : fmtTime(s.getHours(), s.getMinutes())}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: INK_LG }}>{ev.title}</span>
                  {ev.video && <svg width='10' height='10' viewBox='0 0 14 14' fill='none' style={{ flexShrink: 0, opacity: 0.7 }}><path d='M1 4h7v6H1V4z' stroke={INK_LG} strokeWidth='1.4' strokeLinejoin='round'/><path d='M8 5.5l5-2v7l-5-2V5.5z' stroke={INK_LG} strokeWidth='1.4' strokeLinejoin='round'/></svg>}
                </div>
                {!ev.allDay && <div style={{ fontSize: 11, color: '#8a9aa6', marginTop: 1 }}>until {fmtTime(e2.getHours(), e2.getMinutes())}</div>}
              </div>
            </div>
          );
        })}
        {selTks.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontFamily: SERIF_LG, fontSize: 10, letterSpacing: '.14em', color: '#6F8FA7', textTransform: 'uppercase' }}>Due this day</div>
            {selTks.map(tk => {
              const ps = PRI_STYLE[tk.priority] || PRI_STYLE.p3;
              return (
                <div key={tk.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 10, padding: '9px 12px', marginTop: 7, boxShadow: '0 1px 3px rgba(26,46,56,0.05)' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: ps.dot, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 12.5, color: INK_LG }}>{tk.title}</span>
                  <span style={{ fontSize: 9.5, fontWeight: 700, color: ps.color, textTransform: 'uppercase' }}>{tk.priority}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Mobile week view ── day-chip strip + single-day timeline ─────────────────
// Seven 44px columns are unreadable on a phone. Standard mobile-calendar
// pattern instead: a tappable 7-day strip (today highlighted, dot under days
// with events) and the existing DayView timeline for the selected day,
// full-width. Week navigation stays on the topbar arrows.
function MobileWeekView({ events, tasks, weekStart, onEventClick, onSlotClick }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const today = new Date();
  const days  = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const DOW   = ['MON','TUE','WED','THU','FRI','SAT','SUN'];
  const todayIdx = days.findIndex(d => sameDay(d, today));
  const [selIdx, setSelIdx] = useState(todayIdx >= 0 ? todayIdx : 0);
  useEffect(() => {
    const i = Array.from({ length: 7 }, (_, k) => addDays(weekStart, k)).findIndex(d => sameDay(d, new Date()));
    setSelIdx(i >= 0 ? i : 0);
  }, [+weekStart]); // eslint-disable-line react-hooks/exhaustive-deps
  const selDay = days[Math.min(selIdx, 6)];
  const dayHasEvents = days.map(d => events.some(ev => {
    if (ev.allDay) return localDateStr(new Date(ev.start)) <= localDateStr(d) && localDateStr(new Date(ev.end)) >= localDateStr(d);
    return sameDay(new Date(ev.start), d);
  }));
  const allDaySel = events.filter(ev => ev.allDay && localDateStr(new Date(ev.start)) <= localDateStr(selDay) && localDateStr(new Date(ev.end)) >= localDateStr(selDay));
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
      <div style={{ display: 'flex', gap: 4, padding: '8px 10px 7px', borderBottom: `1px solid ${t.bd}`, background: t.panel, flexShrink: 0 }}>
        {days.map((d, i) => {
          const isTod = i === todayIdx;
          const isSel = i === Math.min(selIdx, 6);
          const isWknd = i >= 5;
          return (
            <button key={i} onClick={() => setSelIdx(i)}
              style={{ flex: 1, border: 'none', cursor: 'pointer', fontFamily: F, textAlign: 'center', padding: '6px 0 4px', borderRadius: 10, background: isSel ? INK_LG : isTod ? 'rgba(159,126,79,0.12)' : 'transparent', transition: 'background .12s' }}>
              <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '.06em', color: isSel ? '#AFE1F6' : isWknd ? '#c0ccd4' : '#9eb8c8' }}>{DOW[i]}</div>
              <div style={{ fontFamily: SERIF_LG, fontSize: 16, fontWeight: isSel || isTod ? 700 : 400, color: isSel ? '#f7f4ee' : isTod ? INK_LG : isWknd ? '#b8ac90' : '#3a5060', lineHeight: 1.4 }}>{d.getDate()}</div>
              <div style={{ width: 4, height: 4, borderRadius: '50%', margin: '2px auto 0', background: dayHasEvents[i] ? (isSel ? '#e9d9b8' : GOLD_LG) : 'transparent' }} />
            </button>
          );
        })}
      </div>
      {allDaySel.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '7px 10px', borderBottom: `1px solid ${t.bd}`, background: '#fafcfd', flexShrink: 0 }}>
          {allDaySel.map(ev => {
            const ts = getTypeStyle(ev.type);
            return (
              <div key={ev.id} onClick={() => onEventClick(ev)}
                style={{ fontSize: 11, fontWeight: 700, padding: '5px 9px', borderRadius: 6, background: ts.bg, color: ts.color, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderLeft: '3px solid ' + ts.solid }}>
                {ev.title}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <DayView events={events} tasks={tasks} day={selDay} onEventClick={onEventClick} onSlotClick={onSlotClick} />
      </div>
    </div>
  );
}

// ── Day view ───────────────────────────────────────────────────────────────────
// Greedy lane packing so overlapping events sit side-by-side instead of on top of each other.
function layoutDayEvents(evs) {
  const sorted = [...evs].sort((a, b) => new Date(a.start) - new Date(b.start) || new Date(a.end) - new Date(b.end));
  const out = [];
  let cluster = [], clusterEnd = 0;
  const flush = () => {
    const laneEnds = [];
    const placed = cluster.map(ev => {
      const s = +new Date(ev.start), e = +new Date(ev.end);
      let lane = laneEnds.findIndex(end => end <= s);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(e); } else { laneEnds[lane] = e; }
      return { ev, lane };
    });
    const lanes = laneEnds.length;
    placed.forEach(p => out.push({ ev: p.ev, lane: p.lane, lanes }));
    cluster = [];
  };
  sorted.forEach(ev => {
    const s = +new Date(ev.start), e = +new Date(ev.end);
    if (cluster.length && s >= clusterEnd) flush();
    cluster.push(ev);
    clusterEnd = cluster.length === 1 ? e : Math.max(clusterEnd, e);
  });
  if (cluster.length) flush();
  return out;
}

function DayView({ events, tasks, day, onEventClick, onSlotClick }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const dayEvs   = events.filter(ev => !ev.allDay && sameDay(new Date(ev.start), day));
  const dayTasks = tasks.filter(t => t.due_date && sameDay(new Date(t.due_date + 'T00:00:00'), day));
  const now      = useCurrentTime();
  const isToday  = sameDay(day, new Date());
  const firstH   = HOURS[0];
  const laidOut  = layoutDayEvents(dayEvs);
  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={{ padding: '12px 18px 8px', borderBottom: `1px solid ${t.bd}`, background: '#fafcfd' }}>
        <div style={{ fontFamily: SERIF_LG, fontStyle: 'italic', fontSize: 15.5, color: INK_LG }}>{fmtDateLabel(day)}<span style={{ color: GOLD_LG }}>.</span></div>
        {dayTasks.length > 0 && <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {dayTasks.map(t => {
            const ps = PRI_STYLE[t.priority] || PRI_STYLE.p3;
            return <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 6, background: ps.bg, border: '1px solid ' + ps.border, fontSize: 11, fontWeight: 600, color: ps.color }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: ps.dot, flexShrink: 0 }} />{t.title}
            </div>;
          })}
        </div>}
      </div>
      {/* Fixed-height hour grid with an absolute event overlay so events sit at their real start minute and span their full duration. */}
      <div style={{ position: 'relative' }}>
        {HOURS.map(h => (
          <div key={h} onClick={() => onSlotClick(day, h)} style={{ display: 'flex', borderBottom: '1px solid #f2f4f6', height: ROW_H, cursor: 'pointer' }}>
            <div style={{ width: 60, flexShrink: 0, padding: '6px 10px 0', textAlign: 'right', fontSize: 10, color: '#b8c8d0', fontWeight: 500, whiteSpace: 'nowrap' }}>{fmtTime(h)}</div>
            <div style={{ flex: 1 }} />
          </div>
        ))}
        {isToday && (() => {
          const top = ((now.getHours() - firstH) + now.getMinutes() / 60) * ROW_H;
          if (top < 0) return null;
          return (
            <div style={{ position: 'absolute', left: 60, right: 0, top, height: 2, background: '#e05c5c', zIndex: 4, pointerEvents: 'none' }}>
              <div style={{ position: 'absolute', left: -4, top: -4, width: 9, height: 9, borderRadius: '50%', background: '#e05c5c' }} />
            </div>
          );
        })()}
        <div style={{ position: 'absolute', left: 60, right: 0, top: 0, bottom: 0, pointerEvents: 'none' }}>
          {laidOut.map(({ ev, lane, lanes }) => {
            const ts  = getTypeStyle(ev.type);
            const s   = new Date(ev.start), e = new Date(ev.end);
            const dur = Math.max(0.5, (e - s) / 3600000);
            const top = ((s.getHours() - firstH) + s.getMinutes() / 60) * ROW_H;
            const height = Math.max(28, dur * ROW_H - 4);
            const w = 100 / lanes;
            return (
              <div key={ev.id} onClick={ce => { ce.stopPropagation(); onEventClick(ev); }}
                style={{ position: 'absolute', top: top + 2, height, left: 'calc(' + (lane * w) + '% + 6px)', width: 'calc(' + w + '% - 12px)', borderRadius: 8, background: ts.bg, borderLeft: '3px solid ' + ts.solid, padding: '6px 10px', cursor: 'pointer', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', pointerEvents: 'auto', zIndex: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: ts.color, flex: 1, lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ev.title}</div>
                  {ev.video && <svg width='11' height='11' viewBox='0 0 14 14' fill='none' style={{ flexShrink: 0, opacity: 0.8 }}><path d='M1 4h7v6H1V4z' stroke={ts.color} strokeWidth='1.4' strokeLinejoin='round'/><path d='M8 5.5l5-2v7l-5-2V5.5z' stroke={ts.color} strokeWidth='1.4' strokeLinejoin='round'/></svg>}
                </div>
                {dur >= 0.6 && <div style={{ fontSize: 10.5, color: ts.color, opacity: 0.75, whiteSpace: 'nowrap' }}>{fmtTime(s.getHours(), s.getMinutes())} – {fmtTime(e.getHours(), e.getMinutes())}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Agenda view ────────────────────────────────────────────────────────────────
function AgendaView({ events, tasks, weekStart, onEventClick, team }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const days  = Array.from({ length: 30 }, (_, i) => addDays(weekStart, i));
  const today = new Date();
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
      {days.map(day => {
        const dayEvs   = events.filter(ev => sameDay(new Date(ev.start), day));
        const dayTasks = tasks.filter(t => t.due_date && sameDay(new Date(t.due_date + 'T00:00:00'), day));
        if (!dayEvs.length && !dayTasks.length) return null;
        const isTod = sameDay(day, today);
        return (
          <div key={day.toISOString()} style={{ borderBottom: '1px solid #f0f4f6' }}>
            <div style={{ padding: '8px 18px 5px', display: 'flex', alignItems: 'center', gap: 12, background: isTod ? 'rgba(49,79,92,0.02)' : 'transparent' }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: isTod ? '#314F5C' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: isTod ? '#E0F5FC' : '#314F5C', lineHeight: 1 }}>{day.getDate()}</span>
              </div>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: isTod ? '#314F5C' : '#9eb8c8', textTransform: 'uppercase', letterSpacing: '.07em' }}>{day.toLocaleDateString('en-US', { weekday: 'short', month: 'short' })}</span>
              {isTod && <span style={{ fontSize: 9.5, fontWeight: 700, color: '#E0F5FC', background: t.accent, padding: '2px 7px', borderRadius: 99 }}>Today</span>}
            </div>
            <div style={{ padding: '0 18px 10px', display: 'flex', flexDirection: 'column', gap: 5 }}>
              {dayEvs.map(ev => {
                const ts = getTypeStyle(ev.type);
                return <div key={ev.id} onClick={() => onEventClick(ev)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 9, background: ts.bg, cursor: 'pointer', borderLeft: '3px solid ' + ts.solid, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: ts.color, flex: 1 }}>{ev.title}</div>
                      {ev.video && <svg width='11' height='11' viewBox='0 0 14 14' fill='none' style={{ flexShrink: 0, opacity: 0.8 }}><path d='M1 4h7v6H1V4z' stroke={ts.color} strokeWidth='1.4' strokeLinejoin='round'/><path d='M8 5.5l5-2v7l-5-2V5.5z' stroke={ts.color} strokeWidth='1.4' strokeLinejoin='round'/></svg>}
                    </div>
                    <div style={{ fontSize: 10.5, color: ts.color, opacity: 0.75 }}>{ev.allDay ? 'All day' : fmtTime(new Date(ev.start).getHours(), new Date(ev.start).getMinutes()) + ' – ' + fmtTime(new Date(ev.end).getHours(), new Date(ev.end).getMinutes())}</div>
                  </div>
                  {ev.attendees && ev.attendees.length > 0 && <div style={{ display: 'flex', gap: 3 }}>{ev.attendees.slice(0,3).map(a => { const m = team.find(t => t.initials === a); return m ? <Av key={a} initials={a} color={m.color} size={20} /> : null; })}</div>}
                </div>;
              })}
              {dayTasks.map(t => {
                const ps = PRI_STYLE[t.priority] || PRI_STYLE.p3;
                return <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 9, background: ps.bg, border: '1px solid ' + ps.border }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0f1e28' }}>{t.title}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: ps.color, marginTop: 2 }}>Task due · {ps.label}</div>
                  </div>
                  <PriBadge p={t.priority} />
                </div>;
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Task side panel ────────────────────────────────────────────────────────────
function TaskPanel({ tasks, onAdd, onEdit, onToggleDone, isMobile, team, myInitials }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [scope, setScope] = useState('all');
  const today   = new Date();
  const weekEnd = addDays(today, 7);

  const filtered = tasks.filter(t => {
    if (scope === 'mine') return t.assignee === myInitials;
    if (scope === 'p1')   return t.priority === 'p1';
    return true;
  });

  const thisWeek = filtered.filter(t => t.status !== 'done' && t.due_date && new Date(t.due_date + 'T00:00:00') <= weekEnd);
  const later    = filtered.filter(t => t.status !== 'done' && (!t.due_date || new Date(t.due_date + 'T00:00:00') > weekEnd));
  const done     = filtered.filter(t => t.status === 'done').slice(0, 5);
  const openCount = tasks.filter(t => t.status !== 'done').length;

  function TaskItem({ task }) {
    const ps = PRI_STYLE[task.priority] || PRI_STYLE.p3;
    const _assignee = task.assignees?.[0] || task.assignee;
    const m  = team.find(t => t.initials === _assignee);
    const od = isOverdue(task.due_date) && task.status !== 'done';

    return (
      <div onClick={() => onEdit(task)}
        style={{ padding: '9px 11px', borderRadius: 10,
          border: '1px solid ' + (od ? '#f5c6c6' : task.status === 'done' ? '#edf0f3' : ps.border),
          borderLeft: od ? '3px solid #e05c5c' : undefined,
          background: od ? 'rgba(224,92,92,0.04)' : task.status === 'done' ? '#fafcfd' : ps.bg,
          marginBottom: 5, cursor: 'pointer', opacity: task.status === 'done' ? 0.6 : 1, transition: 'box-shadow .12s' }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(49,79,92,0.1)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          {/* Checkbox */}
          <div onClick={e => { e.stopPropagation(); onToggleDone(task.id); }}
            style={{ width: 16, height: 16, borderRadius: 5, border: '1.5px solid ' + (task.status === 'done' ? '#819D84' : ps.dot), background: task.status === 'done' ? '#819D84' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, cursor: 'pointer', transition: 'all .12s' }}>
            {task.status === 'done' && (
              <svg width='8' height='8' viewBox='0 0 12 12' fill='none' stroke='#fff' strokeWidth='2.5' strokeLinecap='round'><polyline points='2 6 5 9 10 3'/></svg>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: task.status === 'done' ? 400 : 600, color: task.status === 'done' ? '#9eb8c8' : '#0f1e28', lineHeight: 1.4, textDecoration: task.status === 'done' ? 'line-through' : 'none', marginBottom: task.priority || task.due_date || task.assignee ? 5 : 0 }}>
              {task.title}
            </div>
            {(task.priority || task.due_date || task.assignee || task.studio_board_id) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {task.priority && <PriBadge p={task.priority} />}
                {task.due_date && (
                  <span style={{ fontSize: 10, color: od ? '#c62828' : '#9eb8c8', display: 'flex', alignItems: 'center', gap: 3, fontWeight: od ? 600 : 400 }}>
                    <svg width='9' height='9' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round'><rect x='3' y='4' width='18' height='18' rx='2'/><line x1='16' y1='2' x2='16' y2='6'/><line x1='8' y1='2' x2='8' y2='6'/><line x1='3' y1='10' x2='21' y2='10'/></svg>
                    {od ? 'Overdue · ' : ''}{fmtTaskDate(task.due_date)}
                  </span>
                )}
                {m && <Av initials={m.initials} color={m.color} size={16} />}
                {task.studio_board_id && (
                  <button
                    onClick={e => { e.stopPropagation(); sessionStorage.setItem('studio_open_board', task.studio_board_id); const navLinks = [...document.querySelectorAll('a,button,[role="button"]')]; const sl = navLinks.find(el => el.textContent.trim()==='Studio'); if(sl) sl.click(); }}
                    style={{ fontSize:10, color:t.mut, display:'flex', alignItems:'center', gap:2, fontWeight:500, background:'none', border:'none', cursor:'pointer', padding:0, fontFamily:F, transition:'color .1s' }}
                    onMouseEnter={e=>e.currentTarget.style.color='#314F5C'}
                    onMouseLeave={e=>e.currentTarget.style.color='#6F8FA7'}>
                    <svg width='8' height='8' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round'><rect x='3' y='3' width='7' height='7' rx='1'/><rect x='14' y='3' width='7' height='7' rx='1'/><rect x='14' y='14' width='7' height='7' rx='1'/><rect x='3' y='14' width='7' height='7' rx='1'/></svg>
                    Board ↗
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  function Section({ label, items }) {
    if (!items.length) return null;
    return (
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 6px' }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: '#9eb8c8', textTransform: 'uppercase', letterSpacing: '.07em', whiteSpace: 'nowrap' }}>{label}</span>
        <div style={{ flex: 1, height: 1, background: '#e8eef2' }}/>
        <span style={{ fontSize: 9, fontWeight: 600, color: '#b0c4cc' }}>{items.length}</span>
      </div>
        {items.map(t => <TaskItem key={t.id} task={t} />)}
      </div>
    );
  }

  return (
    <div style={{ width: isMobile ? '100%' : 242, flexShrink: 0, borderLeft: `1px solid ${t.bd}`, display: 'flex', flexDirection: 'column', background: '#f8fafc', overflow: 'hidden' }}>
      {/* Panel header */}
      <div style={{ padding: '13px 14px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <svg width='13' height='13' viewBox='0 0 11 11' fill='none' style={{ flexShrink: 0 }}>
            <rect x='.5' y='.5' width='3.5' height='10' rx='1.2' fill='#314F5C'/>
            <rect x='6' y='.5' width='3.5' height='6' rx='1.2' fill='#314F5C'/>
            <rect x='6' y='8.5' width='3.5' height='2' rx='1' fill='#314F5C'/>
          </svg>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f1e28', flex: 1, fontFamily: F }}>Tasks</span>
          {openCount > 0 && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'rgba(49,79,92,0.1)', color: '#314F5C' }}>
              {openCount} open
            </span>
          )}
        </div>
        {/* Filter pills */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 10, background: 'rgba(49,79,92,0.05)', borderRadius: 8, padding: 3 }}>
          {[['all','All'],['mine','Mine'],['p1','P1']].map(([k, lbl]) => (
            <button key={k} onClick={() => setScope(k)}
              style={{ flex: 1, padding: '4px 0', borderRadius: 6, border: 'none', background: scope === k ? '#fff' : 'transparent', color: scope === k ? '#314F5C' : '#9eb8c8', fontSize: 11, fontWeight: scope === k ? 700 : 500, cursor: 'pointer', fontFamily: F, boxShadow: scope === k ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', transition: 'all .12s' }}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {/* Task list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
        <Section label='This week' items={thisWeek} />
        <Section label='Later' items={later} />
        <Section label='Done' items={done} />
        {!thisWeek.length && !later.length && (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 12, color: '#b0c4cc', fontWeight: 500 }}>All clear</div>
          </div>
        )}
        <button onClick={() => onAdd()}
          style={{ width: '100%', padding: '8px', border: '1.5px dashed #d0dce4', borderRadius: 9, background: 'none', cursor: 'pointer', fontSize: 11.5, color: '#9eb8c8', fontFamily: F, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginTop: 4, transition: 'all .12s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#9eb8c8'; e.currentTarget.style.color = '#314F5C'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#d0dce4'; e.currentTarget.style.color = '#9eb8c8'; }}>
          <svg width='10' height='10' viewBox='0 0 12 12' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'><line x1='6' y1='1' x2='6' y2='11'/><line x1='1' y1='6' x2='11' y2='6'/></svg>
          Add task
          <kbd style={{ marginLeft: 4, fontSize: 9, fontWeight: 600, color: '#b0c8d4', background: 'rgba(49,79,92,0.07)', border: '1px solid rgba(49,79,92,0.12)', borderRadius: 3, padding: '1px 4px', fontFamily: F }}>N</kbd>
        </button>
      </div>
    </div>
  );
}

// ── Main Planner component ────────────────────────────────────────────────────
function PlannerBody({ session }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isMobile = useIsMobile();
  const team = useTeamMembers();
  // Derive myInitials from team_members so 'Mine' filter matches stored assignee values
  const myEmail = session?.user?.email || '';
  const myMember = team.find(m => m.email === myEmail);
  const myInitials = myMember
    ? myMember.initials
    : myEmail.split('@')[0].split(/[._-]/).map(function(s) { return s[0]?.toUpperCase() || ''; }).join('').slice(0, 2);
  const [calView,      setCalView]      = useState('Week');
  const [weekStart,    setWeekStart]    = useState(() => startOfWeek(new Date()));
  const [events,       setEvents]       = useState([]);
  const [tasks,        setTasks]        = useState([]);
  const [evForm,       setEvForm]       = useState(null);
  const [peek,         setPeek]         = useState(null); // read-only event sheet
  const [taskForm,     setTaskForm]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [showTasks,    setShowTasks]    = useState(true);
  const [studioBoards, setStudioBoards] = useState([]);

  const loadEvents = useCallback(async () => {
    try {
      const res = await fetch('/api/calendar-events');
      const data = await res.json();
      if (Array.isArray(data) && data.length) {
        setEvents(data.map(ev => ({ ...ev, allDay: ev.all_day, start: new Date(ev.start_at), end: new Date(ev.end_at), desc: ev.description })));
      } else {
        setEvents([]);
      }
    } catch (_e) {
      setEvents([]);
    }
  }, []);

  const loadTasks = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('admin_tasks').select('*').order('created_at', { ascending: false });
      if (!error) setTasks(data || []);
    } catch (_e) { console.error('Planner error:', _e); }
    setLoading(false);
  }, []);

  const loadStudioBoards = useCallback(async () => {
    try {
      const { data } = await supabase.from('studio_boards').select('id,name,color').order('created_at', { ascending: true });
      if (data) setStudioBoards(data);
    } catch (_e) { console.error('Planner error:', _e); }
  }, []);

  useEffect(() => { loadEvents(); loadTasks(); loadStudioBoards(); }, [loadEvents, loadTasks, loadStudioBoards]);

  useEffect(() => {
    const ch = supabase.channel('planner-tasks')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'admin_tasks' }, () => loadTasks())
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [loadTasks]);

  async function saveEvent(ev) {
    const isNew = !ev.id || String(ev.id).startsWith('ev');
    const _endAt = ev.allDay
      ? new Date(new Date(ev.start).setHours(23, 59, 59, 0)).toISOString()
      : ev.end.toISOString();
    const body = { title: ev.title, type: ev.type, all_day: ev.allDay, start_at: ev.start.toISOString(), end_at: _endAt, attendees: ev.attendees, video: ev.video, recur: ev.recur, description: ev.desc };
    if (!isNew) body.id = ev.id;
    try {
      const res = await fetch('/api/calendar-events', {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const saved = await res.json();
      if (res.ok && saved && saved.id) {
        const merged = { ...saved, allDay: saved.all_day, start: new Date(saved.start_at), end: new Date(saved.end_at), desc: saved.description };
        setEvents(prev => prev.filter(e => e.id !== ev.id && e.id !== saved.id).concat(merged));
        setEvForm(null);
        return;
      }
    } catch (_e) { console.error('Planner error:', _e); }
    // optimistic fallback
    setEvents(prev => { const o = prev.filter(e => e.id !== ev.id); return [...o, ev]; });
    setEvForm(null);
  }

  async function deleteEvent(id) {
    try {
      await fetch('/api/calendar-events', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });
    } catch (_e) { console.error('Planner error:', _e); }
    setEvents(prev => prev.filter(e => e.id !== id));
    setEvForm(null);
  }

  // ensureRoom — create/reuse the meeting room WITHOUT starting the call.
  // Used by the "Copy link" / Invite affordance so a shareable link exists
  // before anyone has joined. Returns the room_url (Daily) or null.
  async function ensureRoom(ev) {
    if (ev?.room_url) return ev.room_url;
    try {
      const res = await fetch('/api/calendar-room', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_id: ev.id }),
      });
      const data = await res.json();
      if (!res.ok || !data.room_url) throw new Error(data.error || 'no room');
      setEvents(prev => prev.map(e => e.id === ev.id ? { ...e, room_url: data.room_url } : e));
      return data.room_url;
    } catch (_e) { return null; }
  }

  // Build the shareable "are you ready" invite link from a room_url. Guests who
  // open it land on the GuestRoom pre-join (camera/mic check + knock-to-enter).
  function inviteLinkFromRoomUrl(roomUrl) {
    if (!roomUrl) return '';
    let name = '';
    try { name = new URL(roomUrl).pathname.replace(/^\//, ''); }
    catch (_e) { name = String(roomUrl).split('/').pop() || ''; }
    if (!name) return '';
    return 'https://admin.sanoire.com?room=' + encodeURIComponent(name);
  }

  async function joinRoom(ev) {
    try {
      const res = await fetch('/api/calendar-room', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_id: ev.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not create room.');
      setEvents(prev => prev.map(e => e.id === ev.id ? { ...e, room_url: data.room_url } : e));
      window.dispatchEvent(new CustomEvent('sanoire:startcall', {
        detail: { type: 'calendar', id: ev.id, name: ev.title, roomUrl: data.room_url },
      }));
      // CALL-FIX-07 — broadcast incoming call to attendees on admin-calls
      // channel. Layout.js's incoming-call listener shows them the standard
      // accept/decline popup. Without this, only the caller's own overlay
      // fired and attendees never saw they had an incoming call.
      try {
        const me = (await supabase.auth.getUser()).data?.user;
        const callerName = me?.email?.split('@')[0] || 'Team member';
        const callerInitials = callerName.slice(0, 2).toUpperCase();
        const ch = supabase.channel('admin-calls');
        await ch.subscribe();
        await ch.send({
          type: 'broadcast',
          event: 'incoming_call',
          payload: {
            callerName, callerInitials,
            roomUrl: data.room_url,
            roomName: 'calendar-' + ev.id,
            type: 'calendar',
            eventId: ev.id,
            eventTitle: ev.title,
            recipients: (ev.attendees && ev.attendees.length) ? ev.attendees : 'all',
          },
        });
        setTimeout(function () { supabase.removeChannel(ch); }, 5000);
      } catch (_e) { /* non-blocking */ }
      setEvForm(null);
    } catch (e) { console.error('joinRoom:', e.message); alert('Could not start video call: ' + e.message); }
  }

  async function saveTask(t) {
    const data = { title: t.title, status: t.status || 'todo', priority: t.priority || 'p2', due_date: t.due_date || null, tags: t.tags || [], assignee: (t.assignees && t.assignees[0]) || t.assignee || null, description: t.description || '', studio_board_id: t.studio_board_id || null };
    try {
      if (t.id) {
        const { data: upd } = await supabase.from('admin_tasks').update(data).eq('id', t.id).select().single();
        if (upd) setTasks(prev => prev.map(x => x.id === t.id ? upd : x));
      } else {
        const { data: created } = await supabase.from('admin_tasks').insert([data]).select().single();
        if (created) setTasks(prev => [created, ...prev]);
      }
    } catch (_e) { console.error('Planner error:', _e); }
    setTaskForm(null);
  }

  async function toggleDone(id) {
    const t = tasks.find(x => x.id === id);
    if (!t) return;
    const ns = t.status === 'done' ? 'todo' : 'done';
    const { data: upd } = await supabase.from('admin_tasks').update({ status: ns }).eq('id', id).select().single();
    if (upd) setTasks(prev => prev.map(x => x.id === id ? upd : x));
  }

  // ── Studio integration ─────────────────────────────────────────────────────
  async function openBoard(boardId, taskId, taskTitle) {
    if (boardId === 'new') {
      // Create a board named after the task and link it
      if (!taskTitle) return;
      try {
        const BOARD_COLORS = ['#314F5C','#819D84','#B99C6D','#6F8FA7','#5856d6'];
        const { data:newBoard } = await supabase.from('studio_boards')
          .insert({ name: taskTitle, color: BOARD_COLORS[studioBoards.length % BOARD_COLORS.length], created_by:'planner' })
          .select().single();
        if (newBoard && taskId) {
          await supabase.from('admin_tasks').update({ studio_board_id: newBoard.id }).eq('id', taskId);
          setTasks(prev => prev.map(t => t.id === taskId ? {...t, studio_board_id: newBoard.id} : t));
          setStudioBoards(prev => [...prev, newBoard]);
          boardId = newBoard.id;
        } else return;
      } catch (_e) { return; }
    }
    sessionStorage.setItem('studio_open_board', boardId);
    // Click the Studio nav link
    const navLinks = [...document.querySelectorAll('a, button, [role="button"]')];
    const studioLink = navLinks.find(el => el.textContent.trim() === 'Studio');
    if (studioLink) { studioLink.click(); } else { window.location.href = '/studio'; }
  }

  function navLabel() {
    if (calView === 'Week') {
      const end = addDays(weekStart, 6);
      return weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' – ' + end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    if (calView === 'Day')   return fmtDateLabel(weekStart);
    if (calView === 'Month') return weekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    return 'Next 30 days';
  }
  function navPrev() {
    if (calView === 'Week')        setWeekStart(d => addDays(d, -7));
    else if (calView === 'Day')    setWeekStart(d => addDays(d, -1));
    else if (calView === 'Month')  setWeekStart(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  }
  function navNext() {
    if (calView === 'Week')        setWeekStart(d => addDays(d, 7));
    else if (calView === 'Day')    setWeekStart(d => addDays(d, 1));
    else if (calView === 'Month')  setWeekStart(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }
  function goToday() { setWeekStart(startOfWeek(new Date())); }

  // ── Keyboard shortcuts ──────────────────────────────────────────────────────
  React.useEffect(() => {
    function onKey(e) {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'n' || e.key === 'N') { e.preventDefault(); setTaskForm({}); }
      if (e.key === 'e' || e.key === 'E') { e.preventDefault(); setEvForm({}); }
      if (e.key === 't' || e.key === 'T') { e.preventDefault(); goToday(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line
  function handleSlotClick(day, h) {
    const s = new Date(day); s.setHours(h, 0, 0, 0);
    setEvForm({ start: s, end: new Date(s.getTime() + 3600000) });
  }

  const segBtn = (active) => ({ padding: '4px 12px', borderRadius: 6, border: 'none', fontSize: 11.5, fontWeight: active ? 700 : 500, cursor: 'pointer', fontFamily: F, background: active ? '#fff' : 'transparent', color: active ? '#314F5C' : '#9eb8c8', transition: 'all .12s', boxShadow: active ? '0 1px 3px rgba(0,0,0,0.09)' : 'none' });
  const navBtn = { width: 28, height: 28, borderRadius: 7, border: '1px solid #e4edf3', background: t.panel, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7a9aaa', transition: 'all .12s' };
  // Mobile: fill the viewport. The old hardcoded 'calc(100dvh - 180px)' left a
  // dead band above the bottom nav. Measure where the card actually starts and
  // stretch it to just above the fixed bottom nav (58px + safe-area cushion).
  const rootRef = useRef(null);
  const [mobH, setMobH] = useState(null);
  useEffect(() => {
    // Desktop has a ConsoleShell masthead above the card; mobile has the fixed
    // bottom nav below it. Measure the card's real offset and fill exactly.
    const measure = () => {
      if (!rootRef.current) return;
      const top = rootRef.current.getBoundingClientRect().top;
      setMobH(Math.max(360, window.innerHeight - top - (isMobile ? 58 : 8)));
    };
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); };
  }, [isMobile]);
  const contentH = mobH ? mobH + 'px' : (isMobile ? 'calc(100dvh - 150px)' : 'calc(100vh - 105px)');

  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', height: contentH, background: t.panel, margin: '-16px -20px -22px', borderRadius: 0, border: 'none', overflow: 'hidden', fontFamily: F, boxShadow: 'none' }}>

      {/* Topbar — single row on desktop; two stacked rows on mobile
          (row 1: full-width view switcher · row 2: ‹ label › + Today + Add) */}
      <div style={{ padding: isMobile ? '10px 12px' : '12px 18px 11px', borderBottom: `1px solid ${t.bd}`, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 8 : 0, flexShrink: 0, background: t.panel }}>

        {/* View switcher — Month / Week / Day / Agenda */}
        <div style={{ display: 'flex', background: 'rgba(49,79,92,0.06)', borderRadius: 9, padding: '3px', gap: 1, flexShrink: 0 }}>
          {CAL_VIEWS.map(v => <button key={v} onClick={() => setCalView(v)} style={{ ...segBtn(calView === v), ...(isMobile ? { flex: 1, textAlign: 'center' } : {}) }}>{v}</button>)}
        </div>

        {/* Divider — desktop only */}
        {!isMobile && <div style={{ width: 1, height: 20, background: '#e4edf3', margin: '0 14px', flexShrink: 0 }} />}

        {/* Nav cluster: ← label → + Today (+ Add pinned right on mobile) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, minWidth: 0 }}>
          <button style={navBtn} onClick={navPrev} onMouseEnter={e => e.currentTarget.style.background='#f4f7f9'} onMouseLeave={e => e.currentTarget.style.background='#fff'}>
            <svg width='9' height='9' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round'><polyline points='15 18 9 12 15 6'/></svg>
          </button>
          <span style={{ fontFamily: SERIF_LG, fontStyle: 'italic', fontSize: 15.5, fontWeight: 400, color: INK_LG, minWidth: isMobile ? 'unset' : 150, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{navLabel()}<span style={{ color: GOLD_LG }}>.</span></span>
          <button style={navBtn} onClick={navNext} onMouseEnter={e => e.currentTarget.style.background='#f4f7f9'} onMouseLeave={e => e.currentTarget.style.background='#fff'}>
            <svg width='9' height='9' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round'><polyline points='9 18 15 12 9 6'/></svg>
          </button>
          <button onClick={goToday}
            style={{ marginLeft: 8, padding: '4px 11px', border: '1px solid #e4edf3', borderRadius: 7, background: t.panel, fontSize: 11.5, color: '#5a7a8a', cursor: 'pointer', fontFamily: F, transition: 'all .12s', fontWeight: 500, flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.style.background='#f4f7f9'} onMouseLeave={e => e.currentTarget.style.background='#fff'}>Today
          </button>
          {isMobile && <div style={{ flex: 1 }} />}
          {isMobile && (
            <button onClick={() => setEvForm({})}
              title="New event (E)"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: t.accent, color: '#E0F5FC', border: 'none', borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: F, boxShadow: '0 1px 0 rgba(0,0,0,0.15) inset, 0 2px 6px rgba(49,79,92,0.25)', flexShrink: 0 }}>
              <svg width='11' height='11' viewBox='0 0 12 12' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'><line x1='6' y1='1' x2='6' y2='11'/><line x1='1' y1='6' x2='11' y2='6'/></svg>
              Add
            </button>
          )}
        </div>

        {!isMobile && (() => {
          const ws = weekStart, we = new Date(+startOfWeek(new Date()) + 7 * 86400000);
          const wkStart = startOfWeek(new Date());
          const evCount = events.filter(ev => { const s = new Date(ev.start); return s >= wkStart && s < we; }).length;
          const dueCount = tasks.filter(tk => tk.status !== 'done' && tk.due_date).length;
          void ws;
          return (
            <div style={{ display: 'flex', gap: 6, marginLeft: 14 }}>
              <span style={{ fontFamily: SERIF_LG, fontSize: 11, color: INK_LG, background: PAPER_DEEP_LG, border: `1px solid ${GOLD_HAIR_LG}`, borderRadius: 999, padding: '3px 10px' }}>{evCount} event{evCount === 1 ? '' : 's'} this week</span>
              <span style={{ fontFamily: SERIF_LG, fontSize: 11, color: INK_LG, background: PAPER_DEEP_LG, border: `1px solid ${GOLD_HAIR_LG}`, borderRadius: 999, padding: '3px 10px' }}>{dueCount} task{dueCount === 1 ? '' : 's'} open</span>
            </div>
          );
        })()}
        <div style={{ flex: 1, display: isMobile ? 'none' : 'block' }} />

        {/* Right side (desktop only): task toggle + new task + add event */}
        {!isMobile && (
          <button onClick={() => setShowTasks(v => !v)} title={showTasks ? 'Hide tasks' : 'Show tasks'}
            style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid ' + (showTasks ? 'rgba(49,79,92,0.25)' : '#e4edf3'), background: showTasks ? 'rgba(49,79,92,0.08)' : '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: showTasks ? '#314F5C' : '#9eb8c8', transition: 'all .12s', marginRight: 8 }}>
            <svg width='11' height='11' viewBox='0 0 11 11' fill='none'>
              <rect x='.5' y='.5' width='3.5' height='10' rx='1.2' fill='currentColor'/>
              <rect x='6' y='.5' width='3.5' height='6' rx='1.2' fill='currentColor'/>
              <rect x='6' y='8.5' width='3.5' height='2' rx='1' fill='currentColor'/>
            </svg>
          </button>
        )}
        {!isMobile && (
          <button onClick={() => setTaskForm({})}
            title="New task (N)"
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'transparent', border: '1px solid #e4edf3', borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: F, color: '#6F8FA7', marginRight: 6, transition: 'all .12s', flexShrink: 0 }}
            onMouseEnter={e => { e.currentTarget.style.background='#f4f7f9'; e.currentTarget.style.borderColor='#c8d8e0'; }}
            onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.borderColor='#e4edf3'; }}>
            <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round'><polyline points='9 11 12 14 22 4'/><path d='M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11'/></svg>
            Task
          </button>
        )}
        {!isMobile && (
          <button onClick={() => setEvForm({})}
            title="New event (E)"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: t.accent, color: '#E0F5FC', border: 'none', borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: F, boxShadow: '0 1px 0 rgba(0,0,0,0.15) inset, 0 2px 6px rgba(49,79,92,0.25)', flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.style.background='#253e48'} onMouseLeave={e => e.currentTarget.style.background='#314F5C'}>
            <svg width='11' height='11' viewBox='0 0 12 12' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'><line x1='6' y1='1' x2='6' y2='11'/><line x1='1' y1='6' x2='11' y2='6'/></svg>
            Add event
          </button>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          {loading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b0c0c8', fontSize: 13 }}>Loading…</div>
          ) : calView === 'Month' ? (
            isMobile
              ? <MobileMonthView events={events} tasks={tasks} weekStart={weekStart} onEventClick={ev => setPeek(ev)} onSlotClick={handleSlotClick} />
              : <MonthView events={events} tasks={tasks} weekStart={weekStart} onDayClick={d => { setWeekStart(d); setCalView('Day'); }} onEventClick={ev => setPeek(ev)} />
          ) : calView === 'Week' ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
              {isMobile
                ? <MobileWeekView events={events} tasks={tasks} weekStart={weekStart} onEventClick={ev => setPeek(ev)} onSlotClick={handleSlotClick} />
                : <WeekView events={events} tasks={tasks} weekStart={weekStart} onEventClick={ev => setPeek(ev)} onSlotClick={handleSlotClick} />}
              {events.filter(ev => { const s = new Date(ev.start); const e = new Date(ev.end); const we = new Date(+weekStart + 6*86400000); return s <= we && e >= weekStart; }).length === 0 && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', gap: 6 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 13, background: 'rgba(49,79,92,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 2 }}>
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='rgba(49,79,92,0.3)' strokeWidth='1.5' strokeLinecap='round'><rect x='3' y='4' width='18' height='18' rx='2'/><line x1='16' y1='2' x2='16' y2='6'/><line x1='8' y1='2' x2='8' y2='6'/><line x1='3' y1='10' x2='21' y2='10'/></svg>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(49,79,92,0.38)', fontFamily: F }}>No events this week</div>
                  <div style={{ fontSize: 11.5, color: 'rgba(49,79,92,0.25)', fontFamily: F }}>Click any time slot · or press <kbd style={{ background: 'rgba(49,79,92,0.07)', border: '1px solid rgba(49,79,92,0.13)', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600, color: 'rgba(49,79,92,0.4)', fontFamily: F }}>E</kbd> to add one</div>
                </div>
              )}
            </div>
          ) : calView === 'Day' ? (
            <DayView events={events} tasks={tasks} day={weekStart} onEventClick={ev => setPeek(ev)} onSlotClick={handleSlotClick} />
          ) : (
            <AgendaView events={events} tasks={tasks} weekStart={weekStart} onEventClick={ev => setPeek(ev)} team={team} />
          )}
        </div>
        {showTasks && !isMobile && (
          <TaskPanel tasks={tasks} onAdd={() => setTaskForm({})} onEdit={t => setTaskForm(t)} onToggleDone={toggleDone} isMobile={isMobile} team={team} myInitials={myInitials} />
        )}
      </div>

      {peek && (
        <EventPeek event={peek} team={team} isMobile={isMobile}
          onClose={() => setPeek(null)}
          onEdit={() => { setEvForm(peek); setPeek(null); }}
          onDelete={() => { if (window.confirm('Delete this event?')) { deleteEvent(peek.id); setPeek(null); } }}
          joinRoom={joinRoom} ensureRoom={ensureRoom} inviteLink={inviteLinkFromRoomUrl} />
      )}
      {evForm !== null && (
        <EventForm event={evForm.id ? evForm : (evForm.start ? evForm : null)} onSave={saveEvent} onDelete={deleteEvent} onClose={() => setEvForm(null)} isMobile={isMobile} team={team} joinRoom={joinRoom} />
      )}
      {taskForm !== null && (
        <TaskForm task={taskForm.id ? taskForm : null} onSave={saveTask} onClose={() => setTaskForm(null)} team={team} boards={studioBoards} onOpenBoard={openBoard} />
      )}
    </div>
  );
}

export default function Planner(props) {
  if (props && props.embed) return <PlannerBody session={props.session} />;
  return <ConsoleShell title="Planner"><PlannerBody session={props && props.session} /></ConsoleShell>;
}
